import { createUuid } from '@notifi/uuid/browser';
import { addressTextMatches, addressesMatch } from '@/lib/address-match';
import { apiPatch, apiPost, operationsClient } from '@/lib/api';
import { estimateTemplateEndpoints, opsEstimateEndpoints } from '@/lib/api/endpoints';
import { effectiveApprovalMethod } from '@/lib/estimates/approval-method';
import {
  computeEstimatePricingPreview,
  type EstimateLinePricingPreview,
} from '@/lib/sales/estimate-pricing-preview';
import type { CustomFieldSchema } from '@/types/custom-fields';
import {
  buildCustomFieldValues,
  customFieldValueToDraftString,
  getDefaultCustomFieldKeys,
  getInitialCustomFieldDraftValue,
} from '@/components/organisms/custom-fields/custom-field-form-utils';
import type {
  CreateEstimateDto,
  CustomerApprovalMethod,
  Estimate,
  EstimateLineItem,
  EstimateBookingType,
  EstimateRecurringConfig,
  EstimateRecurringEndType,
  EstimateRecurringFrequency,
  EstimateRecurringIntervalUnit,
} from '@/types/estimate';
import type { EstimateTemplateLineItem } from '@/types/estimateTemplate';
import type { TaxResolutionResult } from '@/types/tax';
import { normalizeDisplayConfig, type DisplayConfig } from '@/types/pricebook';
import {
  membershipPlanSummaryFromPurchaseSnapshot,
  type MembershipPlanSummary,
} from '@/lib/membership/format-plan-summary';
import { chargesRevenue, unitPriceOf, visibleToCustomerFlag } from '@/lib/sales/line-economics';
import { reconcileFlatMarkupPercent } from '@/lib/sales/line-item-pricing';
import { flattenCustomLinePricing, isManualLine } from '@/lib/sales/pricingMode';
import type { PricingTreatment } from '@/lib/sales/service-rollup';
import { readTermsSelection, toTermsPayload, type TermsSelection } from '@/lib/terms';
import { getCommercialPrimaryContact } from '@/lib/contacts/commercialContactPerson';
import { membershipSetupFee } from '@notifi/shared-types/membership-line';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PricebookLineItem {
  id: string;
  itemVersion: number;
  name: string;
  code: string;
  type: string;
  unit: string;
  description?: string;
  basePrice: number;
  laborRate?: number;
  costBasis?: number;
  markupPercent?: number;
  /** MATERIAL / EQUIPMENT — whether the picked line bills at its own price. */
  chargeable?: boolean;
  /** NOT-590 — the relationship's own answer, when this arrives as a component. */
  pricingTreatment?: PricingTreatment | null;
  markupOverride?: number | null;
  /** EQUIPMENT — hidden from the customer's document by the operator. */
  hiddenFromCustomer?: boolean;
  priceStrategy?: string;
  taxable?: boolean;
  taxLineType?: string | null;
  /** Typed by hand in the picker rather than picked from the catalog. */
  isCustom?: boolean;
  /** The catalog's tax class and accounts, snapshotted onto the line. */
  taxCategory?: string | null;
  taxCode?: string | null;
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
  estimatedHours?: number;
  /** SERVICE — what an hour COSTS us. See `PricebookItem.laborCostRate`. */
  laborCostRate?: number;
  /** SERVICE — the labor rows behind the hours. See `EditableLineItem`. */
  laborCostLines?: import('@/types/pricebook').ServiceLaborLine[];
  dynamicPricingConfig?: import('@/types/estimate').DocumentDynamicPricingConfig;
  displayConfig?: DisplayConfig | null;
  membershipPlanSummary?: MembershipPlanSummary;
  /** NOT-605 — plan sold by an in-cart MEMBERSHIP line, so the preview can
   *  synthesize a prospective membership and unlock member rates on siblings. */
  membershipPlanId?: string | null;
  membershipSetupFee?: number;
  membershipSetupFeeFor?: string;
  membershipSetupFeePlanId?: string;
  membershipBillingOptionId?: string | null;
  membershipCustomerChoosesBillingOption?: boolean;
  /** NOT-605 — item-level member pricing carried into the estimate preview. */
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  /** NOT-590 — stable per-line key linking a service to its component lines. */
  lineKey?: string;
  parentLineKey?: string;
  componentDisplayMode?: 'ITEMIZED' | 'ROLLED_UP';
  /** An optional component lands unselected: offered, not sold. */
  isOptional?: boolean;
  isSelected?: boolean;
  configurationName?: string;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
}

export interface EditableLineItem {
  /** Service-call visit-price credit: false = always charged (e.g. materials);
   * true/undefined = credits toward work when the customer proceeds. */
  isServiceCallFee?: boolean;
  /**
   * Typed by hand rather than picked from the catalog.
   *
   * Its `lineItemId` is a client-minted UUID that resolves to nothing in the
   * pricebook, which is what makes the distinction worth recording: the line is
   * saved as MANUAL so nothing downstream treats it as a catalog sale.
   *
   * Carried explicitly instead of re-derived from an empty `code`, which is
   * what every reader had to guess from before.
   */
  isCustom?: boolean;
  _rowId: string;
  /** One-save bridge for rows persisted before rowId existed. */
  _legacySourceIndex?: number;
  lineItemId: string;
  itemVersion: number;
  name: string;
  code: string;
  type: string;
  unit: string;
  description?: string;
  basePrice: number;
  /**
   * The unit price the operator set for THIS estimate — the persisted spelling
   * of the spec's `unitPrice`, and what the operations pricing engine resolves
   * ahead of everything else.
   *
   * Kept separate from `basePrice`, which stays the pricebook's original so a
   * line can always be put back to what the catalog says. `null`/absent is "no
   * override", which is NOT the same as an override of 0.
   */
  manualPriceOverride?: number | null;
  laborRate: number;
  costBasis: number;
  /**
   * Where a hand-typed job part's cost came from. `costBasis` remains the
   * effective unit cost used by pricing and margin; these fields explain the
   * source of that frozen amount without creating a second cost value that can
   * drift from it.
   */
  costSource?: 'DIRECT' | 'SUPPLIER';
  costSupplierId?: string;
  costSupplierName?: string;
  markupPercent: number;
  /**
   * MATERIAL / EQUIPMENT: billed at its own price and shown to the customer.
   * `false` bills nothing and hides the row while the cost still counts.
   * Undefined is chargeable — an estimate saved before the flag existed must
   * keep billing exactly as it did.
   *
   * The catalog's answer, and only the default for `pricingTreatment`.
   */
  chargeable?: boolean;
  /**
   * How this document charges for the part. `included_in_service` charges zero
   * on this row because the money is inside the service carrying it; absent
   * derives from `chargeable`. See the double-charging rule in service-rollup.
   */
  pricingTreatment?: PricingTreatment | null;
  /** Markup percentage this line uses instead of the catalog item's own. */
  markupOverride?: number | null;
  /**
   * EQUIPMENT: hidden from the customer's document. Default false — equipment
   * prints even at zero, because what is being installed is the substance of
   * the offer. Materials need no such control; chargeability decides them.
   */
  hiddenFromCustomer?: boolean;
  priceStrategy: string;
  taxable: boolean;
  /** Catalog default retained so the editor can explain estimate-only overrides. */
  pricebookTaxable?: boolean;
  taxLineType?: string | null;
  /**
   * How the tax engine classifies this line, and where its money posts.
   *
   * Snapshotted from the catalog for a picked line and typed by the operator on
   * a custom one, but carried by the SAME fields either way — the document is
   * what the accounting reads, so a line that cannot state its own account is a
   * line that posts to none. They round-trip through save and reload for the
   * same reason every other snapshot field does: a value the payload omits is a
   * value the next save erases.
   */
  taxCategory?: string | null;
  taxCode?: string | null;
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
  /**
   * The class this line stated before Taxable was unticked, so re-ticking gives
   * it back instead of a re-derived default.
   *
   * EDITOR STATE, never sent and never stored: the payload builders enumerate
   * their fields explicitly, so this cannot reach a DTO the API would reject
   * for it. It is the estimate's equivalent of the ref the pricebook form keeps
   * for the same purpose — held on the row rather than in the component because
   * the Taxable tick lives outside the tax editor, and the editor unmounts
   * whenever the row is collapsed.
   */
  taxLineTypeBeforeExempt?: string | null;
  discountAmount?: number;
  discountPercent?: number;
  displayConfig?: DisplayConfig | null;
  quantity: number;
  isOptional: boolean;
  isSelected: boolean;
  /**
   * EDITOR STATE only: the offer choice to restore if a standalone part is
   * made chargeable again before save. Payload builders enumerate fields, so
   * this never reaches the API.
   */
  optionStateBeforeUnbilled?: { isOptional: boolean; isSelected: boolean };
  addedBy?: string;
  addedByRole?: 'OFFICE' | 'TECH';
  isUpsell?: boolean;
  upsellContext?: string;
  sortOrder?: number;
  /**
   * Hours of work on this service. DYNAMIC: they drive the price. FLAT: they
   * are cost only and the typed price is untouched.
   */
  estimatedHours?: number;
  /** What an hour of this service COSTS us — never what it sells for. */
  laborCostRate?: number;
  /**
   * The labor rows a FLAT service's hours are made of — duration, rate type and
   * rate, one per crew. The EDITOR's form of the same fact `estimatedHours` and
   * `laborCostRate` hold reduced; costing reads the reduced pair, so the two
   * are always written together (see `updateLaborCostLines`).
   */
  laborCostLines?: import('@/types/pricebook').ServiceLaborLine[];
  dynamicPricingConfig?: import('@/types/estimate').DocumentDynamicPricingConfig;
  membershipPlanSummary?: MembershipPlanSummary;
  /** NOT-605 — plan id of an in-cart MEMBERSHIP line, so the preview can build a
   *  prospective membership and price siblings at member rates before enrollment. */
  membershipPlanId?: string | null;
  membershipSetupFee?: number;
  membershipSetupFeeFor?: string;
  membershipSetupFeePlanId?: string;
  membershipBillingOptionId?: string | null;
  membershipCustomerChoosesBillingOption?: boolean;
  /** NOT-605 — item-level member pricing; consumed by the estimate preview so
   *  the form total matches the saved (backend) member-priced estimate. */
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  /** NOT-590 — stable per-line key linking a service to its component lines. */
  lineKey?: string;
  parentLineKey?: string;
  componentDisplayMode?: 'ITEMIZED' | 'ROLLED_UP';
  /** Chosen configuration when this line came from a configurable equipment item. */
  configurationName?: string;
  /** Existing physical units serviced by this line (new EQUIPMENT lines create units later). */
  equipmentIds?: string[];
  /** NOT-628 — the plan's first included visit was delivered with the sale. */
  membershipDeliveredServiceIds?: string[];
  /** Audited explanation when a selected unit is outside the document location. */
  equipmentLocationOverrideReason?: string;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  /** Client-side creation context shown while composing a job. */
  provenance?: {
    source: 'ESTIMATE' | 'JOB_TYPE';
    label: string;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function computeLinePreview(
  item: EditableLineItem,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE',
  taxResolution: TaxResolutionResult | null,
): EstimateLinePricingPreview {
  return computeEstimatePricingPreview({
    lineItems: [item],
    taxResolution,
    pricingMode,
    discountValue: 0,
    discountType: '$',
    membershipInfo: null,
  }).lineItemPreviews[0]!;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function toEditableLineItem(item: PricebookLineItem): EditableLineItem {
  const canOfferLine = Boolean(item.parentLineKey) || chargesRevenue(item);
  const customerChoosesMembershipBilling = item.membershipCustomerChoosesBillingOption === true;
  return {
    _rowId: createUuid(),
    lineItemId: item.id,
    itemVersion: item.itemVersion ?? 1,
    name: item.name,
    code: item.code,
    type: item.type,
    unit: item.unit || 'each',
    description: item.description,
    basePrice: item.basePrice ?? 0,
    laborRate: item.laborRate ?? 0,
    // The catalog's defaults for the line's TIME. Both were being dropped here,
    // so a service that quotes 2 hours in the pricebook landed on the estimate
    // quoting none.
    estimatedHours: item.estimatedHours,
    laborCostRate: item.laborCostRate,
    laborCostLines: item.laborCostLines,
    costBasis: item.costBasis ?? 0,
    markupPercent: reconcileFlatMarkupPercent({
      type: item.type,
      priceStrategy: item.priceStrategy,
      basePrice: item.basePrice,
      costBasis: item.costBasis,
      markupPercent: item.markupPercent,
    }),
    // A part added to an estimate is a part being sold, so it starts billed.
    // Only an explicit `false` from the picker carries over.
    chargeable: item.chargeable !== false,
    // The relationship's answer, when there is one, is the thing that decides —
    // see pricingTreatmentOf. Left null the line's own flag applies.
    pricingTreatment: item.pricingTreatment ?? null,
    markupOverride: item.markupOverride ?? null,
    hiddenFromCustomer: item.hiddenFromCustomer === true,
    priceStrategy: item.priceStrategy ?? 'FLAT',
    taxable: item.taxable ?? true,
    // Only the non-taxable default needs provenance in the editor. Omitting the
    // ordinary `true` default also keeps custom lines from being mislabeled as
    // pricebook-derived.
    pricebookTaxable: item.taxable === false ? false : undefined,
    taxLineType: item.taxLineType ?? null,
    // Kept, so the line is saved as what it is rather than as a catalog sale.
    // The picker is the only thing that knows; by the time the row exists all
    // that is left to infer from is a blank code.
    isCustom: item.isCustom === true,
    // The catalog's tax class and accounts travel with the line. Dropping them
    // here left every estimate line posting to no GL account and re-deriving a
    // tax class from its bare type, whatever the pricebook item said.
    taxCategory: item.taxCategory ?? null,
    taxCode: item.taxCode ?? null,
    glAccount: item.glAccount ?? null,
    glCogs: item.glCogs ?? null,
    glExpenseAccount: item.glExpenseAccount ?? null,
    glClass: item.glClass ?? null,
    discountAmount: 0,
    discountPercent: 0,
    displayConfig: normalizeDisplayConfig(item.displayConfig),
    quantity: 1,
    isOptional: canOfferLine
      ? customerChoosesMembershipBilling || Boolean(item.isOptional ?? false)
      : false,
    isSelected: canOfferLine
      ? customerChoosesMembershipBilling
        ? false
        : Boolean(item.isSelected ?? true)
      : true,
    membershipPlanSummary: item.membershipPlanSummary,
    membershipPlanId: item.membershipPlanId ?? null,
    membershipSetupFee: item.membershipSetupFee,
    membershipSetupFeeFor: item.membershipSetupFeeFor,
    membershipSetupFeePlanId: item.membershipSetupFeePlanId,
    membershipBillingOptionId: item.membershipBillingOptionId ?? null,
    membershipCustomerChoosesBillingOption: item.membershipCustomerChoosesBillingOption === true,
    memberPrice: item.memberPrice ?? null,
    memberDiscountPercent: item.memberDiscountPercent ?? null,
    dynamicPricingConfig: item.dynamicPricingConfig,
    imageUrl: item.imageUrl,
    imagePreviewUrl: item.imagePreviewUrl,
    // EVERY line gets a stable identity, minted here when the source has none.
    //
    // The picker only keys the lines it expands — a service and its parts —
    // because until NOT-638 nothing else could produce two lines of one catalog
    // item: the pickers excluded what the document already carried. Now they
    // don't, and `lineItemId` alone stops being an identity. The server falls
    // back to a lineItemId-keyed map when a line carries no `lineKey`
    // (estimates.service, customer approval), so two OPTIONAL lines of the same
    // catalog item would tick and untick together — the customer deselects the
    // second copper fitting and loses both. A key each keeps them addressable
    // one at a time.
    //
    // Only ever a fallback: a key the picker already assigned is what the
    // matching `parentLineKey` on that service's part lines points at, so it
    // must survive untouched or the parts orphan.
    lineKey: item.lineKey ?? `line-${createUuid()}`,
    parentLineKey: item.parentLineKey,
    componentDisplayMode: item.componentDisplayMode,
    configurationName: item.configurationName,
  };
}

/**
 * The line's tax class and GL accounts, in the shape the save payload wants.
 *
 * Written as one helper because both payload builders (full and lite) must
 * agree: a field one of them omits is a field that survives a full save and
 * vanishes on a quick one, which is the worst of the three possible behaviours.
 *
 * A blank is OMITTED rather than sent as an empty string, and that is what
 * clears the value: the server reads an absent key as null. Sending `''` would
 * store an empty account code, which is not the same as having no account and
 * is not something any reader downstream knows how to ignore.
 */
function taxAndAccountingPayload(item: EditableLineItem) {
  const carry = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  };

  return {
    ...(carry(item.taxCategory) && { taxCategory: carry(item.taxCategory) }),
    ...(carry(item.taxCode) && { taxCode: carry(item.taxCode) }),
    ...(carry(item.glAccount) && { glAccount: carry(item.glAccount) }),
    ...(carry(item.glCogs) && { glCogs: carry(item.glCogs) }),
    ...(carry(item.glExpenseAccount) && {
      glExpenseAccount: carry(item.glExpenseAccount),
    }),
    ...(carry(item.glClass) && { glClass: carry(item.glClass) }),
  };
}

/**
 * One editable row, as the API stores a document line.
 *
 * THE mapper for a sales-document line, called by the estimate save and by the
 * estimate-template save alike (see `templateLineItemToDto`, which drops only
 * the handful of fields a template cannot hold). Two mappers is what let the
 * template editor and the estimate editor disagree about the same line: a field
 * one of them forgot was a field the backend's `whitelist: true` stripped, so
 * the template quietly stored less than the estimate it exists to produce.
 */
export function buildEstimateLinePayload(
  item: EditableLineItem,
  index: number,
  preview?: LineItemPreviewLike,
): EstimateLineItem {
  // Selection belongs to a customer offer. A standalone included/non-chargeable
  // part is required work even when its stored legacy flags say otherwise. A
  // linked component remains eligible because it can be optional within its
  // parent service. Memberships are chargeable estimate lines too: when staff
  // explicitly marks one optional, that exact choice must survive the save.
  const canOfferLine = Boolean(item.parentLineKey) || chargesRevenue(item);
  return {
    ...(item._legacySourceIndex != null && {
      legacySourceIndex: item._legacySourceIndex,
    }),
    rowId: item._rowId,
    lineItemId: item.lineItemId,
    itemVersion: item.itemVersion,
    name: item.name,
    code: item.code || '',
    type: item.type,
    unit: item.unit || 'each',
    description: item.description,
    basePrice: item.basePrice,
    // The operator's price for THIS estimate. Sent on every line — omitting
    // it would revert the override on the next save — and `null` is how it
    // is removed, which is not the same as pricing the line at $0.
    manualPriceOverride: item.manualPriceOverride ?? null,
    laborRate: item.laborRate,
    costBasis: item.costBasis,
    markupPercent: item.markupPercent,
    // Sent on every line, not only when false: a per-line override the
    // payload omits is an override that reverts on the next save.
    chargeable: item.chargeable !== false,
    // Same reasoning, and the field that actually decides whether this line
    // charges the customer — see the double-charging rule.
    pricingTreatment: item.pricingTreatment ?? null,
    markupOverride: item.markupOverride ?? null,
    hiddenFromCustomer: item.hiddenFromCustomer === true,
    // Stated on every line, from the one flag above. Sending only one
    // spelling leaves the other stale on a line that already carried it —
    // and the server merges an update over the stored line, so a stale
    // `visibleToCustomer: false` outlives the operator turning the eye
    // back on. Money is blind to this; what prints is not.
    visibleToCustomer: item.hiddenFromCustomer !== true,
    // NOT-605 — persist the item-level member pricing snapshot so a saved
    // estimate reloads with member rates already applied in the preview.
    memberPrice: item.memberPrice ?? null,
    memberDiscountPercent: item.memberDiscountPercent ?? null,
    // NOT-605 — persist the in-cart membership plan link (join-and-save).
    ...(item.membershipPlanId && { membershipPlanId: item.membershipPlanId }),
    ...(item.membershipSetupFee != null && { membershipSetupFee: item.membershipSetupFee }),
    ...(item.membershipSetupFeeFor && { membershipSetupFeeFor: item.membershipSetupFeeFor }),
    ...(item.membershipSetupFeePlanId && {
      membershipSetupFeePlanId: item.membershipSetupFeePlanId,
    }),
    ...(item.membershipBillingOptionId && {
      membershipBillingOptionId: item.membershipBillingOptionId,
    }),
    membershipCustomerChoosesBillingOption: item.membershipCustomerChoosesBillingOption === true,
    currency: 'USD',
    priceStrategy: item.priceStrategy,
    taxable: item.taxable,
    ...(typeof item.pricebookTaxable === 'boolean' && {
      pricebookTaxable: item.pricebookTaxable,
    }),
    taxLineType: item.taxLineType ?? undefined,
    ...taxAndAccountingPayload(item),
    displayConfig: normalizeDisplayConfig(item.displayConfig),
    // Existing documents may carry a legacy membership quantity above one.
    // Preserve it during an unrelated edit; the server accepts only that exact
    // stored value, while every new membership row is still created as one.
    quantity: item.quantity,
    isOptional: canOfferLine
      ? item.membershipCustomerChoosesBillingOption === true || item.isOptional
      : false,
    isSelected: canOfferLine
      ? item.membershipCustomerChoosesBillingOption === true
        ? false
        : item.isSelected
      : true,
    // A hand-typed line was never in the catalog, and saying it was
    // makes it indistinguishable from one that was. MANUAL is the
    // value the enum already carries for this, and what the template
    // path already records for the same kind of line.
    source: item.isCustom ? ('MANUAL' as const) : ('CATALOG' as const),
    discountAmount: item.discountAmount ?? 0,
    discountPercent: item.discountPercent ?? 0,
    ...(item.addedBy && { addedBy: item.addedBy }),
    ...(item.addedByRole && { addedByRole: item.addedByRole }),
    ...(item.isUpsell && { isUpsell: true }),
    ...(item.upsellContext && { upsellContext: item.upsellContext }),
    sortOrder: item.sortOrder ?? index,
    ...(typeof item.estimatedHours === 'number' && { estimatedHours: item.estimatedHours }),
    ...(typeof item.laborCostRate === 'number' && { laborCostRate: item.laborCostRate }),
    ...(item.laborCostLines?.length ? { laborCostLines: item.laborCostLines } : {}),
    ...(item.dynamicPricingConfig && { dynamicPricingConfig: item.dynamicPricingConfig }),
    ...(item.imageUrl && { imageUrl: item.imageUrl }),
    // NOT-590 — carry the service ↔ component linkage through the save so
    // a part line keeps pointing at the service that produced it.
    ...(item.lineKey && { lineKey: item.lineKey }),
    ...(item.parentLineKey && { parentLineKey: item.parentLineKey }),
    ...(item.componentDisplayMode && {
      componentDisplayMode: item.componentDisplayMode,
    }),
    ...(item.configurationName && { configurationName: item.configurationName }),
    // [] means "cover no units". Omitting the field means "auto-select the
    // matching units at this address" to the authoritative membership pricer.
    ...(Array.isArray(item.equipmentIds) && { equipmentIds: item.equipmentIds }),
    ...(item.membershipDeliveredServiceIds?.length
      ? { membershipDeliveredServiceIds: item.membershipDeliveredServiceIds }
      : {}),
    ...(item.equipmentLocationOverrideReason?.trim() && {
      equipmentLocationOverrideReason: item.equipmentLocationOverrideReason.trim(),
    }),
    lineSubtotal: preview?.lineSubtotal ?? unitPriceOf(item) * item.quantity,
    lineTax: preview?.lineTax ?? 0,
    lineTotal: preview?.lineTotal ?? unitPriceOf(item) * item.quantity,
  };
}

/**
 * The pricing treatment off a raw snapshot, or null.
 *
 * Anything unrecognised reads as null rather than being coerced: a value nobody
 * defined must fall back to the catalog default, not pick a charging rule at
 * random.
 */
function readPricingTreatment(value: unknown): PricingTreatment | null {
  return value === 'included_in_service' || value === 'separate_charge' ? value : null;
}

export function existingLineItemToEditable(
  raw: Record<string, unknown>,
  legacySourceIndex?: number,
): EditableLineItem {
  const purchaseSnapshot =
    raw.membershipPurchaseSnapshot && typeof raw.membershipPurchaseSnapshot === 'object'
      ? (raw.membershipPurchaseSnapshot as Record<string, unknown>)
      : null;
  const chargeable = raw.chargeable !== false;
  const pricingTreatment = readPricingTreatment(raw.pricingTreatment);
  // A linked component can be an optional piece of its parent even when the
  // parent carries its revenue. A standalone unbilled part is already required
  // work, so a stale/imported deselection must not remove its cost on reopen.
  const canOfferLine =
    Boolean(raw.parentLineKey) ||
    chargesRevenue({ type: String(raw.type ?? ''), chargeable, pricingTreatment });
  const hasPersistedRowId =
    (typeof raw.rowId === 'string' && Boolean(raw.rowId)) ||
    (typeof raw.snapshotId === 'string' && Boolean(raw.snapshotId));
  const isMembershipLine = isMembershipLineType(String(raw.type ?? ''));
  // Every tell a stored line gives — see `isManualLine`. This hydrator is
  // shared with the JOB form, whose custom lines say `source: 'custom'` and
  // carry a `code: 'CUSTOM'` sentinel, so reading only the estimate's spelling
  // reloaded them as catalog sales and the next save wrote them back as such.
  const isCustom = isManualLine({
    source: raw.source as string | null | undefined,
    isCustom: raw.isCustom === true,
    code: raw.code as string | null | undefined,
  });
  // A hand-typed line prices FLAT. One that was saved DYNAMIC — the editor
  // used to allow it — is read back flat AT THE PRICE IT WAS DERIVING, so
  // reopening the estimate cannot move its total.
  const pricing = flattenCustomLinePricing({
    type: String(raw.type ?? ''),
    isCustom,
    priceStrategy: raw.priceStrategy as string | null | undefined,
    basePrice: Number(raw.basePrice ?? 0),
    laborRate: Number(raw.laborRate ?? 0),
    estimatedHours: raw.estimatedHours != null ? Number(raw.estimatedHours) : undefined,
    costBasis: Number(raw.costBasis ?? 0),
    markupPercent: Number(raw.markupPercent ?? 0),
    quantity: Number(raw.quantity ?? 1),
  });
  return {
    _rowId:
      (raw.rowId && typeof raw.rowId === 'string' ? raw.rowId : null) ??
      (raw.snapshotId && typeof raw.snapshotId === 'string' ? raw.snapshotId : null) ??
      createUuid(),
    ...(isMembershipLine && !hasPersistedRowId && legacySourceIndex != null
      ? { _legacySourceIndex: legacySourceIndex }
      : {}),
    lineItemId: String(raw.lineItemId ?? ''),
    itemVersion: Number(raw.itemVersion ?? 1),
    name: String(raw.name ?? ''),
    code: String(raw.code ?? ''),
    type: String(raw.type ?? ''),
    unit: String(raw.unit ?? 'each'),
    description: String(raw.description ?? ''),
    imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : undefined,
    imagePreviewUrl: typeof raw.imagePreviewUrl === 'string' ? raw.imagePreviewUrl : undefined,
    basePrice: pricing.basePrice,
    // Restored, or a reloaded estimate would show the catalog price on a line
    // it is actually charging something else for — and the next save would drop
    // the override entirely.
    manualPriceOverride: raw.manualPriceOverride != null ? Number(raw.manualPriceOverride) : null,
    laborRate: Number(raw.laborRate ?? 0),
    costBasis: Number(raw.costBasis ?? 0),
    costSource:
      raw.costSource === 'SUPPLIER' && typeof raw.costSupplierId === 'string'
        ? 'SUPPLIER'
        : 'DIRECT',
    costSupplierId:
      typeof raw.costSupplierId === 'string' && raw.costSupplierId ? raw.costSupplierId : undefined,
    costSupplierName:
      typeof raw.costSupplierName === 'string' && raw.costSupplierName
        ? raw.costSupplierName
        : undefined,
    markupPercent: reconcileFlatMarkupPercent({
      type: String(raw.type ?? ''),
      priceStrategy: pricing.priceStrategy,
      basePrice: pricing.basePrice,
      // A saved line carries the operator's price here, and that is the price
      // its markup describes. Without it a reopened estimate showed the
      // catalog's markup and then jumped on the first cost or markup edit.
      manualPriceOverride: raw.manualPriceOverride == null ? null : Number(raw.manualPriceOverride),
      costBasis: Number(raw.costBasis ?? 0),
      markupPercent: Number(raw.markupPercent ?? 0),
    }),
    // A saved line with no flag was billed when it was saved, so it reloads
    // billed. Reading a missing value as `false` would silently stop charging
    // for every line on every estimate written before the flag existed.
    chargeable,
    // A saved line with no treatment falls back to its own chargeable flag,
    // which is exactly how it priced when it was saved.
    pricingTreatment,
    markupOverride: raw.markupOverride != null ? Number(raw.markupOverride) : null,
    // ONE flag in the editor, and `visibleToCustomerFlag` is what resolves it —
    // the shared reader every customer renderer already goes through, so the
    // editor and the document cannot reach different answers about the same
    // line. Holding both spellings in the row is what let them disagree, so the
    // row holds neither copy: it holds the answer.
    //
    // Not the same thing as OR-ing the two spellings, which is what this used
    // to do and is a real bug on a mixed snapshot. The reader's precedence is
    // that `visibleToCustomer` is canonical and authoritative in BOTH
    // directions when it is a boolean, and the legacy spellings are a veto only
    // when it is absent. A stored `{ hiddenFromCustomer: true, visibleToCustomer:
    // true }` — reachable because the server merges a partial update over the
    // stored line — therefore reads VISIBLE to the customer, while the OR read
    // it HIDDEN in the editor. The operator saw a hidden line, the customer saw
    // a shown one, and the next save wrote the editor's answer back over the
    // customer's: a silent, permanent flip. `undefined` lands as NOT hidden,
    // because silence has never hidden a line.
    //
    // Read exactly as it was stored, on EVERY line — including a MATERIAL this
    // document currently charges nothing for. A saved flag is not an
    // inheritance. The catalog's default is shed ONCE, where a line is MADE
    // from a catalog item (`canHoldVisibilityDecision`, asked by the picker's
    // bundle and component expansions and by `addPickedLineItem` /
    // `addConfiguredServicePart`), so by the time a line has been saved its
    // flag has already been through that gate.
    //
    // Asking the question again here asked it of the wrong line. A part the
    // operator hid through the eye while it was charged and then ticked
    // unbilled stores character-for-character what an inherited default stores,
    // and nothing on the estimate tells the two apart afterwards — so a shed on
    // load could only destroy the decision along with the default. It did:
    // hide, untick Chargeable, save, reopen, and the hide was gone. The
    // decision is the irrecoverable half, because nothing in the document
    // remembers it; a stale default that survives is one click away in an eye
    // that appears — already crossed out — the moment the part bills.
    //
    // Nothing starts PRINTING because of this. Keeping a hide only ever takes a
    // row off the customer's document, and an unbilled material was never on
    // one to begin with (`rendersOnCustomerDocument`).
    hiddenFromCustomer:
      visibleToCustomerFlag({
        // Pulled out of an untyped JSON bag exactly the way every other field
        // here is. Which spellings exist is this mapper's business; which one
        // WINS is the reader's, and that is the half that had gone its own way.
        visibleToCustomer:
          typeof raw.visibleToCustomer === 'boolean' ? raw.visibleToCustomer : undefined,
        hiddenFromCustomer:
          typeof raw.hiddenFromCustomer === 'boolean' ? raw.hiddenFromCustomer : undefined,
        showInDetail: typeof raw.showInDetail === 'boolean' ? raw.showInDetail : undefined,
      }) === false,
    // NOT-605 — restore the in-cart membership plan link so a reloaded estimate
    // still synthesizes the prospective membership and prices siblings at member
    // rates (the join-and-save preview), matching the saved backend estimate.
    membershipPlanId:
      raw.membershipPlanId != null
        ? String(raw.membershipPlanId)
        : typeof purchaseSnapshot?.planId === 'string'
          ? purchaseSnapshot.planId
          : null,
    membershipBillingOptionId:
      raw.membershipBillingOptionId != null
        ? String(raw.membershipBillingOptionId)
        : typeof purchaseSnapshot?.billingOptionId === 'string'
          ? purchaseSnapshot.billingOptionId
          : null,
    membershipCustomerChoosesBillingOption: raw.membershipCustomerChoosesBillingOption === true,
    membershipSetupFee: membershipSetupFee(raw),
    membershipSetupFeeFor:
      raw.membershipSetupFeeFor != null ? String(raw.membershipSetupFeeFor) : undefined,
    membershipSetupFeePlanId:
      raw.membershipSetupFeePlanId != null ? String(raw.membershipSetupFeePlanId) : undefined,
    // NOT-605 — restore the item-level member pricing snapshot so the preview,
    // "✦ Member price" badge, and totals reapply member rates on reload (else an
    // edited estimate silently shows base prices until re-saved).
    memberPrice: raw.memberPrice != null ? Number(raw.memberPrice) : null,
    memberDiscountPercent:
      raw.memberDiscountPercent != null ? Number(raw.memberDiscountPercent) : null,
    priceStrategy: pricing.priceStrategy,
    taxable: Boolean(raw.taxable ?? true),
    pricebookTaxable: typeof raw.pricebookTaxable === 'boolean' ? raw.pricebookTaxable : undefined,
    taxLineType: raw.taxLineType != null ? String(raw.taxLineType) : null,
    isCustom,
    // Restored so a load→edit→save round trip keeps them. Without this the
    // reloaded line reads them as unset and the next save clears the classes
    // and accounts the estimate was written with.
    taxCategory: raw.taxCategory != null ? String(raw.taxCategory) : null,
    taxCode: raw.taxCode != null ? String(raw.taxCode) : null,
    glAccount: raw.glAccount != null ? String(raw.glAccount) : null,
    glCogs: raw.glCogs != null ? String(raw.glCogs) : null,
    glExpenseAccount: raw.glExpenseAccount != null ? String(raw.glExpenseAccount) : null,
    glClass: raw.glClass != null ? String(raw.glClass) : null,
    discountAmount: Number(raw.discountAmount ?? 0),
    discountPercent: Number(raw.discountPercent ?? 0),
    displayConfig: normalizeDisplayConfig(raw.displayConfig),
    quantity: Number(raw.quantity ?? 1),
    isOptional: canOfferLine
      ? raw.membershipCustomerChoosesBillingOption === true || Boolean(raw.isOptional ?? false)
      : false,
    isServiceCallFee: (raw['isServiceCallFee'] as boolean | undefined) ?? undefined,
    isSelected: canOfferLine
      ? raw.membershipCustomerChoosesBillingOption === true
        ? false
        : Boolean(raw.isSelected ?? true)
      : true,
    ...(!canOfferLine && raw.isOptional === true
      ? {
          optionStateBeforeUnbilled: {
            isOptional: true,
            isSelected: Boolean(raw.isSelected ?? true),
          },
        }
      : {}),
    addedBy: raw.addedBy != null ? String(raw.addedBy) : undefined,
    addedByRole: raw.addedByRole as 'OFFICE' | 'TECH' | undefined,
    isUpsell: Boolean(raw.isUpsell ?? false),
    upsellContext: raw.upsellContext != null ? String(raw.upsellContext) : undefined,
    sortOrder: raw.sortOrder != null ? Number(raw.sortOrder) : undefined,
    estimatedHours: raw.estimatedHours != null ? Number(raw.estimatedHours) : undefined,
    laborCostRate: raw.laborCostRate != null ? Number(raw.laborCostRate) : undefined,
    laborCostLines: Array.isArray(raw.laborCostLines)
      ? (raw.laborCostLines as import('@/types/pricebook').ServiceLaborLine[])
      : undefined,
    // THE MATERIALIZATION ITSELF, and the reason it has to be restored: this
    // object's PRESENCE is what `isMaterializedDynamicService` answers from, on
    // both sides of the wire. Dropping it on load handed a reopened estimate
    // back the exact shape NOT-632 was — `priceStrategy: 'DYNAMIC'` with nothing
    // to price from — so the preview fell to the legacy `laborRate x hours`
    // derivation over a dynamic service's deliberately zeroed cost columns, and
    // showed the operator a service worth its labor alone (or $0, for a catalog
    // dynamic service, which keeps its rates in `laborLines` and carries no
    // scalar `laborRate` at all) while the saved line and the billing engine
    // both still carried the calculated price. The save has always SENT it
    // (`buildEstimateLinePayload`), the API has always stored and returned it,
    // and only the hydrator forgot — so the next save also wrote the omission
    // back out. It also carries the labor rows the wizard edits and the parts
    // snapshot the price is made of; neither has any other account on the
    // document.
    dynamicPricingConfig:
      raw.dynamicPricingConfig && typeof raw.dynamicPricingConfig === 'object'
        ? (raw.dynamicPricingConfig as import('@/types/estimate').DocumentDynamicPricingConfig)
        : undefined,
    membershipPlanSummary:
      raw.membershipPlanSummary != null
        ? (raw.membershipPlanSummary as MembershipPlanSummary)
        : membershipPlanSummaryFromPurchaseSnapshot(raw.membershipPurchaseSnapshot),
    // NOT-590 — the service ↔ component linkage must survive a load→edit→save
    // round trip, else re-saving an estimate would orphan every part line from
    // the service it belongs to.
    lineKey: typeof raw.lineKey === 'string' ? raw.lineKey : undefined,
    parentLineKey: typeof raw.parentLineKey === 'string' ? raw.parentLineKey : undefined,
    componentDisplayMode:
      raw.componentDisplayMode === 'ROLLED_UP' || raw.componentDisplayMode === 'ITEMIZED'
        ? raw.componentDisplayMode
        : undefined,
    // Must survive a load→edit→save round trip: without it, re-saving an
    // estimate that holds a configured line would post a bare configurable
    // lineItemId and the server would reject it as an unresolved placeholder.
    configurationName: raw.configurationName != null ? String(raw.configurationName) : undefined,
    equipmentIds: Array.isArray(raw.equipmentIds)
      ? raw.equipmentIds.filter((id): id is string => typeof id === 'string')
      : undefined,
    // Also a round-trip field: the server rebuilds the sold plan snapshot from
    // scratch whenever it cannot preserve it (a changed address is enough), and
    // a visit the tech already performed would be scheduled all over again.
    membershipDeliveredServiceIds: Array.isArray(raw.membershipDeliveredServiceIds)
      ? raw.membershipDeliveredServiceIds.filter((id): id is string => typeof id === 'string')
      : undefined,
    equipmentLocationOverrideReason:
      raw.equipmentLocationOverrideReason != null
        ? String(raw.equipmentLocationOverrideReason)
        : undefined,
  };
}

export function buildEstimateLitePayload(args: {
  contactId: string | null;
  billToId?: string | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  primaryContactName?: string;
  primaryContactTitle?: string;
  saStreet: string;
  saCity: string;
  saState: string;
  saZip: string;
  lineItems: EditableLineItem[];
}): CreateEstimateDto {
  const {
    contactId,
    billToId,
    customerName,
    customerEmail,
    customerPhone,
    primaryContactName,
    primaryContactTitle,
    saStreet,
    saCity,
    saState,
    saZip,
    lineItems,
  } = args;

  return {
    contactId: contactId ?? undefined,
    ...(billToId !== undefined ? { billToId } : {}),
    customer: {
      name: customerName,
      email: customerEmail,
      phone: customerPhone,
      primaryContactName: primaryContactName?.trim() || undefined,
      primaryContactTitle: primaryContactTitle?.trim() || undefined,
    },
    serviceAddress: saStreet
      ? { street: saStreet, city: saCity, state: saState, zip: saZip }
      : undefined,
    lineItems: lineItems.map((item, index) => {
      const lineSubtotal = unitPriceOf(item) * item.quantity;
      const canOfferLine = Boolean(item.parentLineKey) || chargesRevenue(item);
      return {
        ...(item._legacySourceIndex != null && {
          legacySourceIndex: item._legacySourceIndex,
        }),
        rowId: item._rowId,
        lineItemId: item.lineItemId,
        itemVersion: item.itemVersion,
        name: item.name,
        code: item.code || '',
        type: item.type,
        unit: item.unit,
        description: item.description,
        basePrice: item.basePrice,
        // Sent on every line for the same reason as the flags below: a price
        // the payload omits is a price that reverts to the catalog on the next
        // save. `null` is how the override is REMOVED — it is not $0.
        manualPriceOverride: item.manualPriceOverride ?? null,
        laborRate: item.laborRate,
        costBasis: item.costBasis,
        markupPercent: item.markupPercent,
        // Sent on every line, not only when false: a per-line override the
        // payload omits is an override that reverts on the next save.
        chargeable: item.chargeable !== false,
        // Same reasoning, and the field that actually decides whether this line
        // charges the customer — see the double-charging rule.
        pricingTreatment: item.pricingTreatment ?? null,
        markupOverride: item.markupOverride ?? null,
        hiddenFromCustomer: item.hiddenFromCustomer === true,
        // Stated on every line, from the one flag above. Sending only one
        // spelling leaves the other stale on a line that already carried it —
        // and the server merges an update over the stored line, so a stale
        // `visibleToCustomer: false` outlives the operator turning the eye
        // back on. Money is blind to this; what prints is not.
        visibleToCustomer: item.hiddenFromCustomer !== true,
        // NOT-605 — persist the item-level member pricing snapshot so a saved
        // estimate reloads with member rates already applied in the preview.
        memberPrice: item.memberPrice ?? null,
        memberDiscountPercent: item.memberDiscountPercent ?? null,
        // NOT-605 — persist the in-cart membership plan link (join-and-save).
        ...(item.membershipPlanId && { membershipPlanId: item.membershipPlanId }),
        ...(item.membershipSetupFee != null && { membershipSetupFee: item.membershipSetupFee }),
        ...(item.membershipSetupFeeFor && { membershipSetupFeeFor: item.membershipSetupFeeFor }),
        ...(item.membershipSetupFeePlanId && {
          membershipSetupFeePlanId: item.membershipSetupFeePlanId,
        }),
        ...(item.membershipBillingOptionId && {
          membershipBillingOptionId: item.membershipBillingOptionId,
        }),
        membershipCustomerChoosesBillingOption:
          item.membershipCustomerChoosesBillingOption === true,
        currency: 'USD',
        priceStrategy: item.priceStrategy,
        taxable: item.taxable,
        ...(typeof item.pricebookTaxable === 'boolean' && {
          pricebookTaxable: item.pricebookTaxable,
        }),
        taxLineType: item.taxLineType ?? undefined,
        ...taxAndAccountingPayload(item),
        displayConfig: normalizeDisplayConfig(item.displayConfig),
        quantity: item.quantity,
        discountAmount: item.discountAmount ?? 0,
        discountPercent: item.discountPercent ?? 0,
        lineSubtotal,
        lineTax: 0,
        lineTotal: lineSubtotal,
        isOptional: canOfferLine
          ? item.membershipCustomerChoosesBillingOption === true || item.isOptional
          : false,
        isSelected: canOfferLine
          ? item.membershipCustomerChoosesBillingOption === true
            ? false
            : item.isSelected
          : true,
        // A hand-typed line was never in the catalog, and saying it was
        // makes it indistinguishable from one that was. MANUAL is the
        // value the enum already carries for this, and what the template
        // path already records for the same kind of line.
        source: item.isCustom ? ('MANUAL' as const) : ('CATALOG' as const),
        sortOrder: item.sortOrder ?? index,
        ...(typeof item.estimatedHours === 'number' && { estimatedHours: item.estimatedHours }),
        ...(typeof item.laborCostRate === 'number' && { laborCostRate: item.laborCostRate }),
        ...(item.laborCostLines?.length ? { laborCostLines: item.laborCostLines } : {}),
        ...(item.dynamicPricingConfig && { dynamicPricingConfig: item.dynamicPricingConfig }),
        ...(item.imageUrl && { imageUrl: item.imageUrl }),
        // NOT-590 — carry the service ↔ component linkage through the save so
        // a part line keeps pointing at the service that produced it.
        ...(item.lineKey && { lineKey: item.lineKey }),
        ...(item.parentLineKey && { parentLineKey: item.parentLineKey }),
        ...(item.componentDisplayMode && {
          componentDisplayMode: item.componentDisplayMode,
        }),
        ...(item.configurationName && { configurationName: item.configurationName }),
        ...(Array.isArray(item.equipmentIds) && { equipmentIds: item.equipmentIds }),
        ...(item.membershipDeliveredServiceIds?.length
          ? { membershipDeliveredServiceIds: item.membershipDeliveredServiceIds }
          : {}),
        ...(item.equipmentLocationOverrideReason?.trim() && {
          equipmentLocationOverrideReason: item.equipmentLocationOverrideReason.trim(),
        }),
      };
    }),
  } as CreateEstimateDto;
}

/**
 * Salesperson attribution for a NEW estimate.
 *
 * Precedence: `preferred` → `current` → the creating user.
 *
 * `preferred` is the rep a chain hands us — today the linked lead's assignee
 * (NOT-584). It outranks `current` because `current` is not evidence of a
 * choice: the effect below seeds the creating user on first render, long before
 * the lead query resolves, so a lead-derived rep that merely tied with `current`
 * could never win. The caller passes `preferred` only while the field is
 * untouched, which is what keeps this from overwriting a real pick — the
 * distinction between "nobody has answered" and "someone answered" lives at the
 * call site, not here.
 *
 * This reverses NOT-509's "never inherit the lead's rep" for the conversion
 * path only. Its candidate filter survives intact and still applies to
 * `preferred`: a lead assigned to an accountant does not make an accountant the
 * salesperson, it falls through to the creator.
 *
 * While the roster is still loading no membership check is possible, and the
 * two kinds of id part ways: an id already in hand is taken at face value
 * rather than wiped, but `preferred` is not inherited at all until it can be
 * checked — otherwise a fast submit persists an ineligible rep before the
 * filter ever runs.
 * Create-only — an existing estimate keeps its saved salesperson.
 */
export function resolveCreateSalespersonId({
  current,
  preferred,
  currentUserId,
  candidateIds,
  candidatesLoaded,
}: {
  current: string;
  preferred?: string | null;
  currentUserId: string | null | undefined;
  candidateIds: string[];
  /**
   * Whether the candidate roster has actually come back. A non-empty list
   * implies it, but an empty one is ambiguous — still in flight, or a tenant
   * with nobody eligible — and the two want opposite answers.
   */
  candidatesLoaded?: boolean;
}): string {
  const loaded = candidatesLoaded ?? candidateIds.length > 0;
  const isCandidate = (id: string) => !loaded || candidateIds.includes(id);
  // `preferred` is a GUESS about someone new (the linked lead's rep), so it
  // waits until it can be checked: inheriting an ineligible rep and letting a
  // fast submit persist it is the exact NOT-509 mismatch this is meant to
  // prevent. `current` and `currentUserId` are existing answers and stay
  // permissive while loading — dropping them mid-flight would wipe a value the
  // user is looking at.
  if (preferred && loaded && isCandidate(preferred)) return preferred;
  if (current && isCandidate(current)) return current;
  return currentUserId && isCandidate(currentUserId) ? currentUserId : '';
}

function isMembershipLineType(type: string | null | undefined): boolean {
  return (
    String(type ?? '')
      .trim()
      .toUpperCase() === 'MEMBERSHIP'
  );
}

interface EstimateDraftFormValues {
  estimateNumber?: string;
  title?: string;
  contactId: string | null;
  linkedLeadId: string;
  source: string;
  leadSource: string;
  leadSourceId: string;
  salespersonId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  primaryContactName?: string;
  primaryContactTitle?: string;
  customerAddress: string;
  scopeOfWork?: string;
  notes: string;
  terms: TermsSelection;
  /** Document template to render through; null uses the tenant default. */
  docTemplateId: string | null;
  tagIds: string[];
  internalNotes: string;
  validUntil?: string;
  discountValue: number;
  discountType: '$' | '%';
  discountReason: string;
  promotionId?: string | null;
  customerApprovalMethod: CustomerApprovalMethod | '';
  requireSignature: boolean;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  depositValue: number;
  depositType: '$' | '%';
  depositRequired: boolean;
  bookingType?: EstimateBookingType;
  recurringFrequency?: EstimateRecurringFrequency;
  recurringInterval?: number;
  recurringIntervalUnit?: EstimateRecurringIntervalUnit;
  recurringStartDate?: string;
  recurringDaysOfWeek?: number[];
  recurringEndType?: EstimateRecurringEndType;
  recurringEndAfterCount?: number;
  recurringEndOnDate?: string;
  lineItems: EditableLineItem[];
}

export function deriveEstimateSettingsDefaults(settings?: {
  defaultMessage?: string | null;
  depositAmount?: number | null;
  depositType?: string | null;
  signatureRequired?: boolean | null;
  defaultApprovalMethod?: CustomerApprovalMethod | null;
  expirationDays?: number | null;
}): {
  notes?: string;
  depositValue?: number;
  depositType?: '$' | '%';
  requireSignature?: boolean;
  customerApprovalMethod?: CustomerApprovalMethod;
  validUntil: string;
} | null {
  if (!settings) return null;
  const days = settings.expirationDays ?? 30;
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + days);

  return {
    notes: settings.defaultMessage ?? undefined,
    depositValue:
      settings.depositAmount && settings.depositAmount > 0 ? settings.depositAmount : undefined,
    depositType: settings.depositType === 'fixed' ? '$' : '%',
    requireSignature:
      typeof settings.signatureRequired === 'boolean' ? settings.signatureRequired : undefined,
    // NOT-584 — resolved through the shared rule so this seed and the settings
    // readout can never disagree about what a new estimate pre-selects.
    customerApprovalMethod:
      effectiveApprovalMethod(settings.defaultApprovalMethod, settings.signatureRequired) ??
      undefined,
    validUntil: nextDate.toISOString().split('T')[0],
  };
}

function normalizeAmountType(value: unknown): '$' | '%' {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase();

  if (
    normalized === 'PERCENT' ||
    normalized === 'PERCENTAGE' ||
    normalized === 'PERCENTAGE_RATE' ||
    normalized === '%'
  ) {
    return '%';
  }

  return '$';
}

/**
 * NOT-584 (A2) — what the read-only Source field should SHOW.
 *
 * Two different taxonomies live here, not two copies of one value:
 * `metadata.source` is the sales attribution the user owns (MANUAL /
 * INBOUND_LEAD / PROPOSAL / JOB / AI / OTHER) and `Estimate.source` is the
 * platform origin the backend derives (INTERNAL / AI / LEAD / PROPOSAL / JOB /
 * REPORT). They coexist and can disagree — a row stamped PROPOSAL up top can
 * carry MANUAL below.
 *
 * Order matters because of that. Reading the column FIRST would relabel every
 * estimate that already has an attribution: MANUAL would render as "Internal",
 * INBOUND_LEAD as "Lead". Falling back to it only fills the blanks, which is
 * the actual defect — roughly half of existing rows carry no `metadata.source`
 * at all, and the picker that once let someone correct that is gone.
 *
 * What those rows used to show was not "-" but a fabricated "Manual", from a
 * blanket `|| 'MANUAL'` in `applyDraftState` that could not tell a new
 * estimate from an old one with nothing recorded. That guess was also written
 * back on the next save. The default now lives with the duplicate path, where
 * it is a true answer, and this fallback shows the row's real origin instead.
 *
 * DISPLAY ONLY, and deliberately not folded into the draft state that
 * `deriveEstimateFormValuesFromEstimate` returns. `buildEstimatePayload`
 * writes `metadata.source` from that state on EVERY save, so seeding it with
 * the platform origin would quietly rewrite the attribution of any legacy row
 * the next time someone edited an unrelated field — stamping values like
 * INTERNAL and REPORT into a taxonomy that has no such members, and skewing
 * every report keyed off `metadata.source`. The form's state stays strictly
 * what the row actually claims; the fallback is presentation.
 */
export function resolveEstimateSourceForDisplay(
  metadataSource: unknown,
  platformSource: unknown,
): string {
  if (typeof metadataSource === 'string' && metadataSource) return metadataSource;
  return typeof platformSource === 'string' ? platformSource : '';
}

export function deriveEstimateFormValuesFromEstimate(estimate: Estimate): EstimateDraftFormValues {
  const recurringConfig = estimate.recurringConfig;
  const metadata = estimate.metadata ?? {};
  const discountType = normalizeAmountType(estimate.discountType);
  const discountValueRaw = Number(estimate.discountValue);
  // The HEADER discount the operator typed, and NOTHING reconstructed.
  //
  // `Estimate.discountValue` is `Float @default(0)`, so "no header discount"
  // and "a header discount of zero" are the same stored row — which is exactly
  // why an absent or zero value has to read as NO header discount rather than
  // as a cue to recover one from somewhere else. This used to fall back to
  // `estimate.discountTotal`, harmlessly, while that column held the header
  // slice alone.
  //
  // It no longer does: the column is EVERY saving the estimate gives away
  // except the membership's (operations/…/estimates/support/
  // estimate-discount-totals.ts states it), so back-deriving would read a
  // per-LINE saving as a header one. Open an estimate carrying a $200 line
  // discount, change nothing, save — and the round trip INVENTS a $200 header
  // discount on top of the line's own, quietly taking another $200 off the
  // customer's total. Every estimate without a header discount takes that
  // branch, so it is not an edge case.
  //
  // This value is written back by `buildEstimatePayload` on every save (the
  // same read-is-a-write hazard as `source` above), and `JobForm` seeds a job's
  // header discount from it on conversion — so a guess here is a guess that
  // gets persisted twice.
  const discountValue =
    Number.isFinite(discountValueRaw) && discountValueRaw > 0 ? discountValueRaw : 0;
  const depositTypeRaw = estimate.depositType as string | null | undefined;
  const depositValueRaw = Number(estimate.depositValue) || 0;
  let depositType: '$' | '%' = '$';
  let depositValue = 0;

  if (depositTypeRaw) {
    depositType = normalizeAmountType(depositTypeRaw);
    depositValue = depositValueRaw;
  } else {
    const depositAmount = Number(estimate.depositAmount) || 0;
    if (depositAmount > 0) {
      depositType = '$';
      depositValue = depositAmount;
    }
  }

  return {
    estimateNumber: estimate.estimateNumber ?? '',
    title: estimate.title ?? '',
    contactId: estimate.contactId ?? null,
    linkedLeadId: typeof metadata.leadId === 'string' ? metadata.leadId : '',
    // NOT-584 (A2) — the ATTRIBUTION only, never the platform-origin column.
    // This value is round-tripped back into `metadata.source` by
    // `buildEstimatePayload` on every save, so anything seeded here is a
    // write, not a read. The read-only field's fallback for legacy rows lives
    // in `resolveEstimateSourceForDisplay` for exactly that reason.
    source: typeof metadata.source === 'string' ? metadata.source : '',
    leadSource: typeof metadata.leadSource === 'string' ? metadata.leadSource : '',
    leadSourceId: typeof metadata.leadSourceId === 'string' ? metadata.leadSourceId : '',
    salespersonId:
      typeof metadata.salespersonId === 'string'
        ? metadata.salespersonId
        : (estimate.salespersonId ?? ''),
    customerName: estimate.customer?.name ?? '',
    customerEmail: estimate.customer?.email ?? '',
    customerPhone: estimate.customer?.phone ?? '',
    primaryContactName: estimate.customer?.primaryContactName ?? '',
    primaryContactTitle: estimate.customer?.primaryContactTitle ?? '',
    customerAddress: estimate.customer?.address ?? '',
    scopeOfWork: estimate.scopeOfWork ?? '',
    notes: estimate.notes ?? '',
    terms: readTermsSelection(estimate),
    docTemplateId: estimate.docTemplateId ?? null,
    tagIds: estimate.tags?.map((tag) => tag.tagId) ?? [],
    internalNotes: estimate.internalNotes ?? '',
    validUntil: estimate.validUntil ? String(estimate.validUntil).split('T')[0] : undefined,
    discountValue,
    discountType,
    discountReason: estimate.discountReason ? String(estimate.discountReason) : '',
    promotionId: estimate.promotionId ?? null,
    customerApprovalMethod: estimate.customerApprovalMethod
      ? (estimate.customerApprovalMethod as CustomerApprovalMethod)
      : '',
    requireSignature: estimate.signatureRequired !== false,
    pricingMode: estimate.pricingMode as 'STANDARD' | 'TM_OVERRIDE',
    depositValue,
    depositType,
    depositRequired: Boolean(estimate.depositRequired),
    bookingType: estimate.bookingType as EstimateBookingType | undefined,
    recurringFrequency: recurringConfig?.frequency,
    recurringInterval: recurringConfig?.interval,
    recurringIntervalUnit: recurringConfig?.intervalUnit,
    recurringStartDate: recurringConfig?.startDate
      ? String(recurringConfig.startDate).split('T')[0]
      : undefined,
    recurringDaysOfWeek: recurringConfig?.customDays ?? undefined,
    recurringEndType: recurringConfig?.endType,
    recurringEndAfterCount: recurringConfig?.endAfterCount,
    recurringEndOnDate: recurringConfig?.endOnDate
      ? String(recurringConfig.endOnDate).split('T')[0]
      : undefined,
    lineItems: (estimate.lineItems ?? []).map((item, index) =>
      existingLineItemToEditable(item as unknown as Record<string, unknown>, index),
    ),
  };
}

export function deriveEstimateFormValuesFromDuplicate(estimate: Estimate): EstimateDraftFormValues {
  const base = deriveEstimateFormValuesFromEstimate(estimate);
  return {
    ...base,
    estimateNumber: undefined,
    linkedLeadId: '',
    // A duplicate is a NEW estimate somebody is creating by hand, so MANUAL is
    // an answer here rather than a guess — which is why the default lives at
    // this end and not in `applyDraftState`, where it would also fire for an
    // existing row that simply has no attribution and invent one for it.
    source: 'MANUAL',
    leadSource: '',
    leadSourceId: '',
    salespersonId: '',
    validUntil: undefined,
    customerApprovalMethod: '',
    bookingType: undefined,
    recurringFrequency: undefined,
    recurringInterval: undefined,
    recurringIntervalUnit: undefined,
    recurringStartDate: undefined,
    recurringDaysOfWeek: undefined,
    recurringEndType: undefined,
    recurringEndAfterCount: undefined,
    recurringEndOnDate: undefined,
  };
}

/**
 * DERIVED from the stored template line, not a restatement of it.
 *
 * The hand-listed version named only the flat pricing fields, so a template
 * field this mapper had no reason to mention could not even be referenced —
 * which is how bundle linkage, member pricing and the chosen equipment
 * configuration came to be silently dropped between a saved template and the
 * estimate built from it. Deriving means a field added to a template line is
 * visible here the moment it exists.
 */
type TemplateLineItemInput = Partial<Omit<EstimateTemplateLineItem, 'taxable'>> & {
  taxable: boolean;
  /** Stable row id, when the caller carried one. */
  rowId?: string;
  /** Legacy templates kept the selected billing option inside this snapshot. */
  membershipPurchaseSnapshot?: unknown;
  membershipSetupFee?: number;
  membershipSetupFeeFor?: string;
  membershipSetupFeePlanId?: string;
};

export function templateLineItemsToEditable(
  lineItems: TemplateLineItemInput[] | undefined,
): EditableLineItem[] {
  const incompatibleMembership = (lineItems ?? []).find(
    (lineItem) =>
      isMembershipLineType(lineItem.type) &&
      (Number(lineItem.quantity ?? 1) !== 1 ||
        (lineItem.isOptional !== true && lineItem.isSelected === false)),
  );
  if (incompatibleMembership) {
    throw new Error(
      `The estimate template membership "${String(
        incompatibleMembership.name ?? 'Membership',
      )}" uses legacy quantity or selection settings. Repair that template to quantity 1, or mark an unselected membership as optional, before applying it.`,
    );
  }
  return (lineItems ?? []).map((lineItem) => {
    const purchaseSnapshot =
      lineItem.membershipPurchaseSnapshot &&
      typeof lineItem.membershipPurchaseSnapshot === 'object' &&
      !Array.isArray(lineItem.membershipPurchaseSnapshot)
        ? (lineItem.membershipPurchaseSnapshot as Record<string, unknown>)
        : null;
    // The same tells `existingLineItemToEditable` reads. A template line typed
    // in by hand is a hand-typed line on the estimate it seeds, and dropping
    // that here is what let it reload as a catalog sale.
    const isCustom = isManualLine({
      source: lineItem.source,
      code: lineItem.code,
    });
    // Flat-only, at the price it was deriving — see `flattenCustomLinePricing`.
    const pricing = flattenCustomLinePricing({
      type: String(lineItem.type ?? ''),
      isCustom,
      priceStrategy: lineItem.priceStrategy as string | null | undefined,
      basePrice: Number(lineItem.basePrice ?? 0),
      laborRate: Number(lineItem.laborRate ?? 0),
      estimatedHours: lineItem.estimatedHours != null ? Number(lineItem.estimatedHours) : undefined,
      costBasis: Number(lineItem.costBasis ?? 0),
      markupPercent: Number(lineItem.markupPercent ?? 0),
      quantity: Number(lineItem.quantity ?? 1),
    });
    return {
      _rowId:
        (lineItem.rowId && typeof lineItem.rowId === 'string' ? lineItem.rowId : null) ??
        createUuid(),
      lineItemId: String(lineItem.lineItemId ?? ''),
      itemVersion: Number(lineItem.itemVersion ?? 1),
      name: String(lineItem.name ?? ''),
      code: String(lineItem.code ?? ''),
      type: String(lineItem.type ?? ''),
      unit: String(lineItem.unit ?? 'each'),
      description: String(lineItem.description ?? ''),
      imageUrl: typeof lineItem.imageUrl === 'string' ? lineItem.imageUrl : undefined,
      imagePreviewUrl:
        typeof lineItem.imagePreviewUrl === 'string' ? lineItem.imagePreviewUrl : undefined,
      basePrice: pricing.basePrice,
      laborRate: Number(lineItem.laborRate ?? 0),
      costBasis: Number(lineItem.costBasis ?? 0),
      markupPercent: reconcileFlatMarkupPercent({
        type: String(lineItem.type ?? ''),
        priceStrategy: pricing.priceStrategy,
        basePrice: pricing.basePrice,
        // See `existingLineItemToEditable` — the same saved-line reasoning.
        manualPriceOverride:
          lineItem.manualPriceOverride == null ? null : Number(lineItem.manualPriceOverride),
        costBasis: Number(lineItem.costBasis ?? 0),
        markupPercent: Number(lineItem.markupPercent ?? 0),
      }),
      priceStrategy: pricing.priceStrategy,
      isCustom,
      taxable: lineItem.taxable,
      pricebookTaxable:
        typeof lineItem.pricebookTaxable === 'boolean' ? lineItem.pricebookTaxable : undefined,
      taxLineType: lineItem.taxLineType != null ? String(lineItem.taxLineType) : null,
      quantity: Number(lineItem.quantity ?? 1),
      isOptional: Boolean(lineItem.isOptional ?? false),
      isSelected: Boolean(lineItem.isSelected ?? true),
      // Everything below reaches the estimate ONLY through this mapper. The
      // template stores each field correctly; applying the template used to drop
      // them, which is why this list mirrors `existingLineItemToEditable` rather
      // than stopping at the flat pricing fields.
      //
      // `configurationName` is the sharp one: without it a configured equipment
      // line seeds as a bare configurable id, and saving the estimate fails
      // outright with "Configurable equipment must be resolved…".
      configurationName:
        lineItem.configurationName != null ? String(lineItem.configurationName) : undefined,
      // A T&M line is rejected server-side at zero hours, so a template's hours
      // have to survive the seed.
      estimatedHours: lineItem.estimatedHours != null ? Number(lineItem.estimatedHours) : undefined,
      // NOT-590 — the service ↔ component linkage. Dropped, a bundle seeds as
      // unrelated rows and its parts bill on top of the service that already
      // charges for them.
      lineKey: typeof lineItem.lineKey === 'string' ? lineItem.lineKey : undefined,
      parentLineKey:
        typeof lineItem.parentLineKey === 'string' ? lineItem.parentLineKey : undefined,
      componentDisplayMode:
        lineItem.componentDisplayMode === 'ROLLED_UP' ||
        lineItem.componentDisplayMode === 'ITEMIZED'
          ? lineItem.componentDisplayMode
          : undefined,
      pricingTreatment: readPricingTreatment(lineItem.pricingTreatment),
      chargeable: typeof lineItem.chargeable === 'boolean' ? lineItem.chargeable : undefined,
      // ONE flag, through the one reader — same rule as
      // `existingLineItemToEditable`. This used to put BOTH spellings on the
      // seeded row, which is the design the editor exists to prevent: the eye
      // moves only `hiddenFromCustomer`, so the stale `visibleToCustomer` copy
      // outranked it in every `rendersOnCustomerDocument` call the section and
      // the service rollup make on these rows.
      hiddenFromCustomer:
        visibleToCustomerFlag({
          visibleToCustomer: lineItem.visibleToCustomer,
          hiddenFromCustomer: lineItem.hiddenFromCustomer,
        }) === false,
      // NOT-605 — a template applied for a member has to price at member rates.
      memberPrice: lineItem.memberPrice != null ? Number(lineItem.memberPrice) : null,
      memberDiscountPercent:
        lineItem.memberDiscountPercent != null ? Number(lineItem.memberDiscountPercent) : null,
      membershipPlanId:
        lineItem.membershipPlanId != null
          ? String(lineItem.membershipPlanId)
          : typeof purchaseSnapshot?.planId === 'string'
            ? purchaseSnapshot.planId
            : null,
      membershipSetupFee: membershipSetupFee(lineItem),
      membershipSetupFeeFor: lineItem.membershipSetupFeeFor,
      membershipSetupFeePlanId: lineItem.membershipSetupFeePlanId,
      membershipBillingOptionId:
        lineItem.membershipBillingOptionId != null
          ? String(lineItem.membershipBillingOptionId)
          : typeof purchaseSnapshot?.billingOptionId === 'string'
            ? purchaseSnapshot.billingOptionId
            : null,
      displayConfig: normalizeDisplayConfig(lineItem.displayConfig),
      markupOverride: lineItem.markupOverride != null ? Number(lineItem.markupOverride) : null,
      laborCostRate: lineItem.laborCostRate != null ? Number(lineItem.laborCostRate) : undefined,
      laborCostLines: lineItem.laborCostLines,
      // A materialized dynamic service is FLAT on the document — but only while
      // it carries the inputs it was materialized from.
      dynamicPricingConfig: lineItem.dynamicPricingConfig,
      manualPriceOverride:
        lineItem.manualPriceOverride != null ? Number(lineItem.manualPriceOverride) : undefined,
      discountAmount: Number(lineItem.discountAmount ?? 0),
      discountPercent: Number(lineItem.discountPercent ?? 0),
      sortOrder: lineItem.sortOrder != null ? Number(lineItem.sortOrder) : undefined,
    };
  });
}

// Validated against, not cast to: a template row is JSON, so a stale or
// hand-edited recurrence value must not reach the form as if it were legal.
const RECURRING_FREQUENCIES: readonly EstimateRecurringFrequency[] = [
  'WEEKLY',
  'BIWEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'ANNUALLY',
  'CUSTOM',
];
const RECURRING_INTERVAL_UNITS: readonly EstimateRecurringIntervalUnit[] = [
  'DAYS',
  'WEEKS',
  'MONTHS',
  'YEARS',
];
const RECURRING_END_TYPES: readonly EstimateRecurringEndType[] = [
  'INDEFINITE',
  'AFTER_COUNT',
  'ON_DATE',
];

/**
 * The estimate-form fields a template sets, as a patch.
 *
 * A key is present only when the template actually says something. That mirrors
 * the columns exactly — they are nullable and null means "the template has no
 * opinion" — and it is what keeps applying a template from wiping a field the
 * operator has already filled in.
 *
 * `validDays` is the one that changes shape: it is stored as an OFFSET because
 * a template outlives any fixed date, and becomes a real `validUntil` here,
 * measured from today.
 */
export interface EstimateTemplateDefaults {
  jobTypeId?: string;
  jobTypeName?: string;
  scopeOfWork?: string;
  internalNotes?: string;
  validUntil?: string;
  requireSignature?: boolean;
  depositRequired?: boolean;
  depositType?: '$' | '%';
  depositValue?: number;
  discountType?: '$' | '%';
  discountValue?: number;
  discountReason?: string;
  pricingMode?: 'STANDARD' | 'TM_OVERRIDE';
  bookingType?: EstimateBookingType;
  recurringFrequency?: EstimateRecurringFrequency;
  recurringInterval?: number;
  recurringIntervalUnit?: EstimateRecurringIntervalUnit;
  recurringStartDate?: string;
  recurringDaysOfWeek?: number[];
  recurringEndType?: EstimateRecurringEndType;
  recurringEndAfterCount?: number;
  recurringEndOnDate?: string;
}

export function deriveEstimateDefaultsFromTemplate(template: {
  jobTypeId?: string | null;
  jobTypeName?: string | null;
  scopeOfWork?: string | null;
  internalNotes?: string | null;
  validDays?: number | null;
  signatureRequired?: boolean | null;
  depositRequired?: boolean | null;
  depositType?: string | null;
  depositValue?: number | null;
  discountType?: string | null;
  discountValue?: number | null;
  discountReason?: string | null;
  pricingMode?: string | null;
  bookingType?: string | null;
  recurringConfig?: Record<string, unknown> | null;
}): EstimateTemplateDefaults {
  const defaults: EstimateTemplateDefaults = {};

  if (template.jobTypeId) defaults.jobTypeId = template.jobTypeId;
  if (template.jobTypeName) defaults.jobTypeName = template.jobTypeName;
  if (template.scopeOfWork) defaults.scopeOfWork = template.scopeOfWork;
  if (template.internalNotes) defaults.internalNotes = template.internalNotes;

  if (typeof template.validDays === 'number' && template.validDays > 0) {
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + template.validDays);
    defaults.validUntil = validUntil.toISOString().split('T')[0];
  }

  // Explicit false is an answer, so these test for null/undefined rather than
  // falsiness — a template that says "no signature needed" must be able to say it.
  if (template.signatureRequired != null) {
    defaults.requireSignature = template.signatureRequired;
  }
  if (template.depositRequired != null) {
    defaults.depositRequired = template.depositRequired;
  }
  if (template.depositType) {
    defaults.depositType = normalizeAmountType(template.depositType);
  }
  if (typeof template.depositValue === 'number') {
    defaults.depositValue = template.depositValue;
  }
  if (template.discountType) {
    defaults.discountType = normalizeAmountType(template.discountType);
  }
  if (typeof template.discountValue === 'number') {
    defaults.discountValue = template.discountValue;
  }
  if (template.discountReason) defaults.discountReason = template.discountReason;
  if (template.pricingMode === 'STANDARD' || template.pricingMode === 'TM_OVERRIDE') {
    defaults.pricingMode = template.pricingMode;
  }

  if (template.bookingType === 'ONE_OFF' || template.bookingType === 'RECURRING') {
    defaults.bookingType = template.bookingType as EstimateBookingType;
  }

  // Only for a RECURRING template. A stale schedule left on a one-off would
  // describe a series the estimate is not.
  const recurring = template.bookingType === 'RECURRING' ? template.recurringConfig : null;
  if (recurring) {
    const frequency = recurring['frequency'];
    if (RECURRING_FREQUENCIES.includes(frequency as EstimateRecurringFrequency)) {
      defaults.recurringFrequency = frequency as EstimateRecurringFrequency;
    }
    const interval = Number(recurring['interval']);
    if (Number.isFinite(interval) && interval > 0) defaults.recurringInterval = interval;
    const intervalUnit = recurring['intervalUnit'];
    if (RECURRING_INTERVAL_UNITS.includes(intervalUnit as EstimateRecurringIntervalUnit)) {
      defaults.recurringIntervalUnit = intervalUnit as EstimateRecurringIntervalUnit;
    }
    const startDate = recurring['startDate'];
    if (typeof startDate === 'string') {
      defaults.recurringStartDate = startDate.split('T')[0];
    }
    const customDays = recurring['customDays'];
    if (Array.isArray(customDays)) {
      defaults.recurringDaysOfWeek = customDays.filter(
        (day): day is number => typeof day === 'number',
      );
    }
    const endType = recurring['endType'];
    if (RECURRING_END_TYPES.includes(endType as EstimateRecurringEndType)) {
      defaults.recurringEndType = endType as EstimateRecurringEndType;
    }
    const endAfterCount = Number(recurring['endAfterCount']);
    if (Number.isFinite(endAfterCount) && endAfterCount > 0) {
      defaults.recurringEndAfterCount = endAfterCount;
    }
    const endOnDate = recurring['endOnDate'];
    if (typeof endOnDate === 'string') {
      defaults.recurringEndOnDate = endOnDate.split('T')[0];
    }
  }

  return defaults;
}

export function deriveEstimateCustomFieldFormState(
  existingValues: Array<{ fieldKey: string; value: unknown }> | null | undefined,
  estimateCustomFields: CustomFieldSchema[],
): { customFields: Record<string, string>; activeFieldKeys: string[] } {
  const customFields: Record<string, string> = {};
  const activeFieldKeys: string[] = [];

  for (const value of existingValues ?? []) {
    if (value.value == null) continue;
    const schema = estimateCustomFields.find(
      (field) => field.definition.fieldKey === value.fieldKey,
    );
    if (!schema) continue;
    customFields[value.fieldKey] = customFieldValueToDraftString(schema, value.value);
    activeFieldKeys.push(value.fieldKey);
  }

  for (const fieldKey of getDefaultCustomFieldKeys(estimateCustomFields, 'edit')) {
    if (!activeFieldKeys.includes(fieldKey)) activeFieldKeys.push(fieldKey);
    const schema = estimateCustomFields.find((field) => field.definition.fieldKey === fieldKey);
    if (schema && customFields[fieldKey] === undefined) {
      customFields[fieldKey] = getInitialCustomFieldDraftValue(schema);
    }
  }

  return { customFields, activeFieldKeys };
}

export interface LineItemPreviewLike {
  lineSubtotal: number;
  lineTax: number;
  lineTotal: number;
}

interface AddressLike {
  /** Address-row entity id — present on saved contact addresses. */
  id?: string;
  addressId?: string | null;
  title?: string | null;
  label?: string | null;
  street?: string;
  street2?: string | null;
  city?: string;
  state?: string;
  zip?: string;
  /**
   * Present on saved ContactAddress rows and on every persisted snapshot, which
   * is written `country: address.country ?? 'US'`. Declared — but deliberately
   * NOT part of `formatEstimateAddress` — because these two sources disagree by
   * construction on a non-US address, which is exactly why `addressesMatch`
   * excludes it by default (NOT-586).
   */
  country?: string | null;
  // Address-level tax-exemption snapshot fields (present on saved contact
  // addresses). Copied into the document's serviceAddress at creation time.
  isTaxExempt?: boolean;
  taxExemptCertificate?: string | null;
  taxExemptExpiry?: string | null;
  /** Saved address instructions, available to a job created at this location. */
  accessNotes?: string | null;
}

export function formatEstimateAddress(address?: AddressLike | null): string {
  if (!address) return '';
  return [address.street, address.street2, address.city, address.state, address.zip]
    .filter(Boolean)
    .join(', ');
}

export interface EstimateContactAddress extends AddressLike {
  /** Address-row entity id (present on contact addresses) — used to inherit its custom fields. */
  id?: string;
  isPrimary?: boolean;
  isBilling?: boolean;
  isService?: boolean;
}

function matchingSavedAddressIndex(
  addresses: EstimateContactAddress[],
  snapshot: AddressLike,
): number {
  if (snapshot.addressId) {
    return addresses.findIndex((address) => address.id === snapshot.addressId);
  }
  return addresses.findIndex((address) => addressesMatch(address, snapshot));
}

export interface EstimateContactLike {
  _id?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  companyName?: string;
  contactType?: string;
  title?: string | null;
  primaryContactName?: string;
  primaryContactTitle?: string;
  /** Assigned rep — seeds the estimate's salesperson when creating from a contact. */
  assignedTo?: string;
  addresses?: EstimateContactAddress[];
}

interface AddressSelectionState {
  addressMode: 'select' | 'manual';
  selectedAddressIndex: number | null;
  serviceAddress: AddressLike | null;
}

interface BillingSelectionState {
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  customerAddress: string;
  manualBillingAddress?: AddressLike;
}

interface ContactSelectionState {
  contactId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  primaryContactName: string;
  primaryContactTitle: string;
  contactAddresses: EstimateContactAddress[];
  address: AddressSelectionState;
  billing: BillingSelectionState;
}

function findPreferredServiceSelection(
  addresses: EstimateContactAddress[],
  serviceAddress?: AddressLike,
): AddressSelectionState {
  if (addresses.length === 0) {
    return {
      addressMode: serviceAddress ? 'manual' : 'select',
      selectedAddressIndex: null,
      serviceAddress: serviceAddress ?? null,
    };
  }

  const serviceAddresses = addresses.filter((address) => address.isService !== false);
  const primaryServiceIndex = serviceAddresses.findIndex((address) => address.isPrimary);
  const autoServiceAddress =
    primaryServiceIndex >= 0 ? serviceAddresses[primaryServiceIndex] : serviceAddresses[0];

  if (!autoServiceAddress) {
    return {
      addressMode: serviceAddress ? 'manual' : 'select',
      selectedAddressIndex: null,
      serviceAddress: serviceAddress ?? null,
    };
  }

  if (serviceAddress) {
    // NOT-586 — this lookup scans ALL rows on purpose. So does the proposals
    // twin, `findProposalServiceAddressIndex`; the three sales surfaces agree.
    // What still gates is the auto-default selection above, and that is a
    // different question: which row to pick when nothing is saved yet.
    //
    // Here we are identifying the
    // row an already-saved estimate points at, and `isService` is a purpose
    // designation on the contact's address book — the rows to dispatch to by
    // default — not an eligibility rule. Its only backend invariant is
    // `isPrimary ⇒ isService` (contact-addresses.service.ts); nothing validates
    // an estimate's `serviceAddress` against it, and a wholly manual service
    // address belonging to no row at all is legal. So a row flagged non-service
    // after the estimate was written is still the address the user chose.
    //
    // Gating here would not be a smaller fix, it would be a second bug: the
    // no-match branch below restores the address as MANUAL, and
    // `buildEstimatePayload`'s manual branch emits no `addressId` and none of
    // the `isTaxExempt` / `taxExemptCertificate` / `taxExemptExpiry` snapshot
    // the select branch carries. The next save would quietly downgrade an
    // exempt, linked address to a one-off and start taxing it.
    //
    // The blank-picker symptom that motivates gating is a DISPLAY problem and is
    // fixed where it lives: `JobCustomerContactsSection`
    // resolve the selection from the unfiltered rows and carry it as an
    // `unoffered…Option`, so the picker shows the row the payload will write.
    // That surfaces only the row already on the record — it does not let a user
    // newly pick a non-service row.
    const matchedIndex = matchingSavedAddressIndex(addresses, serviceAddress);
    if (matchedIndex >= 0) {
      return {
        addressMode: 'select',
        selectedAddressIndex: matchedIndex,
        serviceAddress: addresses[matchedIndex],
      };
    }
    // Saved address doesn't match any contact address → restore as manual
    return {
      addressMode: 'manual',
      selectedAddressIndex: null,
      serviceAddress,
    };
  }

  const globalIndex = addresses.indexOf(autoServiceAddress);
  return {
    addressMode: 'select',
    selectedAddressIndex: globalIndex >= 0 ? globalIndex : null,
    serviceAddress: autoServiceAddress,
  };
}

function findPreferredBillingSelection(
  addresses: EstimateContactAddress[],
  fallbackAddress: AddressLike | null,
  savedBillingAddress?: string,
  billingAddressStructured?: AddressLike,
): BillingSelectionState {
  // Structured billing address (from estimate.billingAddress JSON) takes priority
  if (billingAddressStructured) {
    if (
      fallbackAddress &&
      (!billingAddressStructured.addressId ||
        billingAddressStructured.addressId === (fallbackAddress.addressId ?? fallbackAddress.id)) &&
      addressesMatch(billingAddressStructured, fallbackAddress)
    ) {
      return {
        billingMode: 'select',
        selectedBillingIndex: null,
        // Same-as-service billing that DOES have a stored structured address,
        // unlike the display-string-only branches further down. Carrying it
        // means switching to manual entry reproduces the address instead of
        // sending `billingAddress: null` over a populated column.
        manualBillingAddress: billingAddressStructured,
        customerAddress: formatEstimateAddress(billingAddressStructured),
      };
    }

    const matchedIndex = matchingSavedAddressIndex(addresses, billingAddressStructured);
    if (matchedIndex >= 0) {
      return {
        billingMode: 'select',
        selectedBillingIndex: matchedIndex,
        customerAddress: formatEstimateAddress(addresses[matchedIndex]),
      };
    }
    return {
      billingMode: 'manual',
      selectedBillingIndex: null,
      customerAddress: formatEstimateAddress(billingAddressStructured),
      manualBillingAddress: billingAddressStructured,
    };
  }

  if (
    fallbackAddress &&
    savedBillingAddress &&
    addressTextMatches(formatEstimateAddress(fallbackAddress), savedBillingAddress)
  ) {
    return {
      billingMode: 'select',
      selectedBillingIndex: null,
      customerAddress: savedBillingAddress,
      // Same-as-service billing. `customerAddress` is only a display string and
      // the null index resolves to nothing, so without this the structure
      // behind it exists nowhere and switching to manual entry has no way to
      // reproduce what the user was looking at.
      manualBillingAddress: fallbackAddress,
    };
  }

  const billingAddresses = addresses.filter((address) => address.isBilling);
  const primaryBillingIndex = billingAddresses.findIndex((address) => address.isPrimary);
  const autoBillingAddress =
    primaryBillingIndex >= 0 ? billingAddresses[primaryBillingIndex] : billingAddresses[0];

  if (savedBillingAddress && billingAddresses.length > 0) {
    const matchedIndex = addresses.findIndex((address) => {
      if (!address.isBilling) return false;
      return addressTextMatches(formatEstimateAddress(address), savedBillingAddress);
    });
    if (matchedIndex >= 0) {
      return {
        billingMode: 'select',
        selectedBillingIndex: matchedIndex,
        customerAddress: formatEstimateAddress(addresses[matchedIndex]),
      };
    }
    return {
      billingMode: 'select',
      selectedBillingIndex: null,
      customerAddress: savedBillingAddress,
      // No manualBillingAddress, and none is needed: this branch is only
      // reachable when `billingAddressStructured` was falsy, so the estimate
      // has no stored structured billing address. A later switch to manual
      // entry sends `billingAddress: null` over a column that is already null
      // — nothing to lose. Only the customer snapshot string exists here, and
      // this key does not write it.
    };
  }

  if (autoBillingAddress) {
    if (fallbackAddress && addressesMatch(autoBillingAddress, fallbackAddress)) {
      return {
        billingMode: 'select',
        selectedBillingIndex: null,
        customerAddress: formatEstimateAddress(autoBillingAddress),
        manualBillingAddress: autoBillingAddress,
      };
    }

    const globalIndex = addresses.indexOf(autoBillingAddress);
    return {
      billingMode: 'select',
      selectedBillingIndex: globalIndex >= 0 ? globalIndex : null,
      customerAddress: formatEstimateAddress(autoBillingAddress),
    };
  }

  // No BILLING addresses — try to match savedBillingAddress against service addresses
  if (savedBillingAddress) {
    const matchedIndex = addresses.findIndex((address) =>
      addressTextMatches(formatEstimateAddress(address), savedBillingAddress),
    );
    if (matchedIndex >= 0) {
      return {
        billingMode: 'select',
        selectedBillingIndex: matchedIndex,
        customerAddress: savedBillingAddress,
      };
    }
    return {
      billingMode: 'select',
      selectedBillingIndex: null,
      customerAddress: savedBillingAddress,
    };
  }

  // Fall back to the current service address
  if (fallbackAddress) {
    const matchedIndex = addresses.findIndex((address) => addressesMatch(address, fallbackAddress));
    if (matchedIndex >= 0) {
      return {
        billingMode: 'select',
        selectedBillingIndex: null,
        customerAddress: formatEstimateAddress(addresses[matchedIndex]),
        manualBillingAddress: addresses[matchedIndex],
      };
    }
  }

  // Last resort: first service address
  const serviceAddresses = addresses.filter((address) => !address.isBilling);
  const autoService = serviceAddresses[0];
  return {
    billingMode: 'select',
    selectedBillingIndex: null,
    customerAddress: formatEstimateAddress(autoService),
    manualBillingAddress: autoService,
  };
}

export function deriveContactSelectionState(contact: EstimateContactLike): ContactSelectionState {
  const contactId = contact._id ?? contact.id ?? '';
  const customerName =
    contact.displayName ?? `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
  const contactAddresses = contact.addresses ?? [];
  const address = findPreferredServiceSelection(contactAddresses);
  const billing = findPreferredBillingSelection(contactAddresses, address.serviceAddress);
  const primaryContact = getCommercialPrimaryContact(contact);

  return {
    contactId,
    customerName,
    customerEmail: contact.primaryEmail ?? '',
    customerPhone: contact.primaryPhone ?? '',
    primaryContactName: contact.primaryContactName ?? primaryContact?.name ?? '',
    primaryContactTitle: contact.primaryContactTitle ?? primaryContact?.title ?? '',
    contactAddresses,
    address,
    billing,
  };
}

export function deriveExistingEstimateAddressState(args: {
  addresses: EstimateContactAddress[];
  serviceAddress?: AddressLike;
  billingAddress?: string;
  billingAddressStructured?: AddressLike;
}): { address: AddressSelectionState; billing: BillingSelectionState } {
  const address = findPreferredServiceSelection(args.addresses, args.serviceAddress);
  const billing = findPreferredBillingSelection(
    args.addresses,
    address.serviceAddress,
    args.billingAddress,
    args.billingAddressStructured,
  );
  return { address, billing };
}

interface DeriveCustomerAttachmentTrackingArgs {
  previous: { id: string; entityType: string }[];
  next: { id: string; entityType: string }[];
  duplicateAttachmentIds?: string[];
}

export function deriveCustomerAttachmentTracking({
  previous,
  next,
  duplicateAttachmentIds = [],
}: DeriveCustomerAttachmentTrackingArgs): {
  excludedTemplateAttachmentIds: string[];
  excludedDuplicateAttachmentIds: string[];
} {
  const removed = previous.filter((attachment) => !next.find((item) => item.id === attachment.id));
  const excludedTemplateAttachmentIds = removed
    .filter((attachment) => attachment.entityType !== 'estimate')
    .map((attachment) => attachment.id);
  const duplicateIdSet = new Set(duplicateAttachmentIds);
  const excludedDuplicateAttachmentIds = removed
    .filter((attachment) => duplicateIdSet.has(attachment.id))
    .map((attachment) => attachment.id);

  return { excludedTemplateAttachmentIds, excludedDuplicateAttachmentIds };
}

/**
 * NOT-584 — the display name for the selected job type: the snapshot sent
 * alongside jobTypeId and the value behind the {job_type} title token. Mirrors
 * the backend, which reads dto.jobTypeName then the resolved JobType row. A
 * cleared job type has no name — falling back to the saved snapshot would
 * resubmit a type the user just removed — while a set-but-archived type (absent
 * from the active list) keeps reading its snapshot.
 */
export function resolveJobTypeName(
  jobTypeId: string,
  jobTypes: { id: string; name: string }[],
  savedJobTypeName?: string | null,
): string {
  if (!jobTypeId) return '';
  return jobTypes.find((jobType) => jobType.id === jobTypeId)?.name ?? savedJobTypeName ?? '';
}

/**
 * Stored `metadata` values that the server validates as an enum.
 *
 * The estimate form echoes the stored metadata blob back inside its update
 * payload (see `editableBaseMetadata` below), so any stored value the server
 * enum rejects turns every subsequent save of that estimate into a 400 — a
 * record permanently unsaveable from the UI because of a value the server
 * itself stored. The demo seed shipped exactly that (`createdByRole: 'seed'`),
 * and an older writer, an import, or a hand-edited row can reproduce it at any
 * time.
 *
 * Mirrors ESTIMATE_CREATED_BY_ROLES in
 * apps/operations/src/modules/sales/estimates/dto/create-estimate.dto.ts — the
 * only enum-constrained key on EstimateMetadataDto today. Add a key here when
 * the server adds an @IsIn/@IsEnum to one.
 */
const ESTIMATE_METADATA_ENUM_VALUES: Record<string, readonly string[]> = {
  createdByRole: ['OFFICE', 'TECH', 'AI', 'CUSTOMER'],
};

/**
 * Strip stored metadata values the server enum would reject.
 *
 * Drops the offending VALUE (the pair never reaches the wire) rather than the
 * key from the echoed set: the key stays echoable, so a conforming value still
 * round-trips and stays editable. Omitting the pair is lossless — the update
 * path merges the patch over the stored blob
 * (`{...existing.metadata, ...patch}` in estimates.service.ts), so a key the
 * payload omits keeps its stored value untouched. Sending `null` instead would
 * be a destructive write of a value the client never asked to change.
 */
export function dropNonConformingEstimateMetadata(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  // The only call site already guards, but this is exported: a caller passing
  // an estimate with no stored metadata should get an empty patch, not a
  // TypeError out of Object.entries.
  if (!metadata) return {};
  return Object.fromEntries(
    Object.entries(metadata).filter(([key, value]) => {
      const allowed = ESTIMATE_METADATA_ENUM_VALUES[key];
      // Unconstrained key, or a cleared value the server treats as absent.
      if (!allowed || value === null || value === undefined) return true;
      return typeof value === 'string' && allowed.includes(value);
    }),
  );
}

interface BuildEstimatePayloadArgs {
  contactId: string | null;
  billToId?: string | null;
  estimateNumber: string;
  title: string;
  jobTypeId?: string;
  jobTypeName?: string;
  poNumber?: string;
  includeTitle: boolean;
  baseMetadata?: Record<string, unknown>;
  linkedLeadId: string;
  source: string;
  leadSource: string;
  leadSourceId: string;
  salespersonId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  primaryContactName?: string;
  primaryContactTitle?: string;
  customerAddress: string;
  addressMode: 'select' | 'manual';
  selectedAddressIndex: number | null;
  saStreet: string;
  saStreet2: string;
  saCity: string;
  saState: string;
  saZip: string;
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  baStreet: string;
  baStreet2: string;
  baCity: string;
  baState: string;
  baZip: string;
  /** The billing address the estimate was saved with (edit only). */
  storedBillingAddress?: AddressLike | null;
  contactAddresses: AddressLike[];
  lineItems: EditableLineItem[];
  lineItemPreviews: Array<LineItemPreviewLike | undefined>;
  subtotal: number;
  taxTotal: number;
  discountAmount: number;
  discountValue: number;
  discountType: '$' | '%';
  discountReason: string;
  promotionId?: string | null;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  customerApprovalMethod: CustomerApprovalMethod | '';
  signatureRequired: boolean;
  grandTotal: number;
  depositAmount: number;
  depositRequired: boolean;
  depositType: '$' | '%';
  depositValue: number;
  bookingType: EstimateBookingType;
  recurringConfigPayload: EstimateRecurringConfig | null;
  validUntil: string;
  scopeOfWork: string;
  notes: string;
  terms: TermsSelection;
  /**
   * Document template to render through; null/undefined uses the tenant
   * default. Optional so payload fixtures that predate the field still type —
   * the estimate form always supplies it.
   */
  docTemplateId?: string | null;
  internalNotes: string;
  tagIds: string[];
  pendingAttachmentId?: string;
  includePendingAttachmentId: boolean;
  isEditMode: boolean;
  // NOT-542 — Business Unit. Anyone who may write the estimate may state it, but
  // only while the estimate is still allowed to choose one: the server rejects a
  // change once the estimate has line items, so a locked form leaves
  // `includeBusinessUnit` false and the field is omitted entirely.
  includeBusinessUnit?: boolean;
  businessUnitId?: string | null;
  derivedBusinessUnitId?: string | null;
  // The unit chosen in the create flow (source SELECTED). Create-only, and
  // separate from the override above so the override's permission gate stands.
  selectedBusinessUnitId?: string;
}

export function buildEstimatePayload({
  contactId,
  billToId,
  estimateNumber,
  title,
  jobTypeId = '',
  jobTypeName = '',
  poNumber = '',
  includeTitle,
  baseMetadata,
  linkedLeadId,
  source,
  leadSource,
  leadSourceId,
  salespersonId,
  customerName,
  customerEmail,
  customerPhone,
  primaryContactName,
  primaryContactTitle,
  customerAddress,
  addressMode,
  selectedAddressIndex,
  saStreet,
  saStreet2,
  saCity,
  saState,
  saZip,
  billingMode,
  selectedBillingIndex,
  baStreet,
  baStreet2,
  baCity,
  baState,
  baZip,
  storedBillingAddress,
  contactAddresses,
  lineItems,
  lineItemPreviews,
  subtotal,
  taxTotal,
  discountAmount,
  discountValue,
  discountType,
  discountReason,
  promotionId = null,
  pricingMode,
  customerApprovalMethod,
  signatureRequired,
  grandTotal,
  depositAmount,
  depositRequired,
  depositType,
  depositValue,
  bookingType,
  recurringConfigPayload,
  validUntil,
  scopeOfWork,
  notes,
  terms,
  docTemplateId,
  internalNotes,
  tagIds,
  pendingAttachmentId,
  includePendingAttachmentId,
  isEditMode,
  includeBusinessUnit = false,
  businessUnitId = null,
  derivedBusinessUnitId = null,
  selectedBusinessUnitId,
}: BuildEstimatePayloadArgs): CreateEstimateDto {
  // NOT-542 — resolve the Business Unit field once. Stating a unit that differs
  // from the derived one (lib/business-units/derivedBusinessUnit) is what pins
  // the estimate to it — including one of a multi-unit job type's own units,
  // which is how the resolver gets corrected; picking the derived unit (or
  // "None") returns it to following the job type.
  const businessUnitFields: Pick<CreateEstimateDto, 'businessUnitId'> = (() => {
    if (!includeBusinessUnit) return {};
    if (businessUnitId != null && businessUnitId !== derivedBusinessUnitId) {
      return { businessUnitId };
    }
    // On edit, send an explicit null to drop any pinned unit; on create, omit so
    // the estimate simply inherits from the job type.
    return isEditMode ? { businessUnitId: null } : {};
  })();
  // An up-front pick is create-time evidence, not a reassignment: send it only
  // while creating, and never let it re-state a unit the update path owns.
  const selectedBusinessUnitFields: Pick<CreateEstimateDto, 'selectedBusinessUnitId'> =
    !isEditMode && selectedBusinessUnitId ? { selectedBusinessUnitId } : {};
  const serviceAddress = (() => {
    if (addressMode === 'manual') {
      return saStreet.trim() && saCity.trim() && saState.trim() && saZip.trim()
        ? {
            street: saStreet.trim(),
            ...(saStreet2.trim() && { street2: saStreet2.trim() }),
            city: saCity.trim(),
            state: saState.trim(),
            zip: saZip.trim(),
          }
        : undefined;
    }

    if (selectedAddressIndex === null) return undefined;
    const address = contactAddresses[selectedAddressIndex];
    if (!address || !address.street || !address.city || !address.state || !address.zip) {
      return undefined;
    }
    return {
      street: String(address.street),
      ...(address.street2 && { street2: String(address.street2) }),
      city: String(address.city),
      state: String(address.state),
      zip: String(address.zip),
      // Snapshot the chosen saved address's tax-exemption status onto the
      // document so the backend tax engine can resolve $0 tax (NOT-94 §16).
      isTaxExempt: address.isTaxExempt ?? false,
      taxExemptCertificate: address.taxExemptCertificate ?? null,
      taxExemptExpiry: address.taxExemptExpiry ?? null,
      ...(address.id && { addressId: address.id }),
    };
  })();

  const billingAddress = (() => {
    if (billingMode === 'manual') {
      return baStreet.trim() && baCity.trim() && baState.trim() && baZip.trim()
        ? {
            street: baStreet.trim(),
            ...(baStreet2.trim() && { street2: baStreet2.trim() }),
            city: baCity.trim(),
            state: baState.trim(),
            zip: baZip.trim(),
          }
        : undefined;
    }
    if (selectedBillingIndex === null) return undefined;
    const address = contactAddresses[selectedBillingIndex];
    if (!address || !address.street || !address.city || !address.state || !address.zip) {
      return undefined;
    }
    return {
      street: String(address.street),
      ...(address.street2 && { street2: String(address.street2) }),
      city: String(address.city),
      state: String(address.state),
      zip: String(address.zip),
      ...(address.id && { addressId: address.id }),
    };
  })();
  const customerBillingAddress = billingAddress
    ? formatEstimateAddress(billingAddress)
    : billingMode === 'manual'
      ? ''
      : customerAddress.trim();
  const serviceAddressText = serviceAddress ? formatEstimateAddress(serviceAddress) : '';
  // Text-level on purpose: this branch is only reachable with no billing address
  // selected, so `customerAddress` is a display snapshot with no structure left
  // to compare. `addressTextMatches` at least normalizes it the same way
  // `addressesMatch` normalizes fields, instead of a fourth local rule.
  const isSameAsServiceBilling =
    billingMode === 'select' &&
    selectedBillingIndex === null &&
    addressTextMatches(customerAddress, serviceAddressText);
  // An estimate saved with billing AT its service address reopens as "same as
  // service" — the form has no other way to show it — and that must not read
  // as the user clearing it. Clearing turns it into "bill the customer", which
  // bills the customer's default billing site: a different place from the one
  // on screen. Only a stored billing address that differs from the service
  // address (the user reset it to service) is cleared.
  const keepsStoredBillingAtService =
    isSameAsServiceBilling &&
    Boolean(storedBillingAddress) &&
    Boolean(serviceAddress) &&
    addressesMatch(storedBillingAddress!, serviceAddress!);
  const shouldClearBillingAddress =
    isEditMode &&
    !billingAddress &&
    (billingMode !== 'select' || isSameAsServiceBilling) &&
    !keepsStoredBillingAtService;
  // Emptying the manual fields on an existing estimate is a deliberate clear, so
  // send an explicit null. Omitting the key leaves the stored snapshot in place
  // (`update` only writes the column when the key is present), which would save
  // "successfully" and redirect to a detail page still showing the address the
  // user just deleted — and still taxing against it.
  const shouldClearServiceAddress = isEditMode && !serviceAddress && addressMode === 'manual';
  const editableMetadataKeys = [
    'source',
    'leadId',
    'salespersonId',
    'createdByRole',
    'techId',
    'leadSource',
    'leadSourceId',
    'proposalId',
    'generatedProposalId',
    'proposalOptionIndex',
    'customerSignatureName',
    'customerSignatureText',
    'customerSignatureAt',
    'aiConfidence',
    'retrievalContext',
  ] as const;
  const editableBaseMetadata = baseMetadata
    ? // Filter BEFORE the emptiness check below, so an estimate whose only
      // stored editable value is a non-conforming one sends no metadata patch
      // at all instead of a patch full of nulls.
      dropNonConformingEstimateMetadata(
        Object.fromEntries(
          editableMetadataKeys.flatMap((key) =>
            Object.prototype.hasOwnProperty.call(baseMetadata, key)
              ? [[key, baseMetadata[key]]]
              : [],
          ),
        ),
      )
    : undefined;
  const hasBaseMetadata = editableBaseMetadata && Object.keys(editableBaseMetadata).length > 0;
  const hasAttributionMetadata = [
    source,
    linkedLeadId,
    leadSource,
    leadSourceId,
    salespersonId,
  ].some((value) => value.trim());
  const metadata =
    hasBaseMetadata || hasAttributionMetadata
      ? {
          ...(editableBaseMetadata ?? {}),
          source: source.trim() || null,
          leadId: linkedLeadId.trim() || null,
          leadSource: leadSource.trim() || null,
          leadSourceId: leadSourceId.trim() || null,
          salespersonId: salespersonId.trim() || null,
        }
      : undefined;

  return {
    ...(contactId && { contactId }),
    ...(billToId !== undefined ? { billToId } : {}),
    customer: {
      name: customerName.trim(),
      email: customerEmail.trim() || undefined,
      phone: customerPhone.trim() || undefined,
      address: customerBillingAddress || undefined,
      primaryContactName: primaryContactName?.trim() || undefined,
      primaryContactTitle: primaryContactTitle?.trim() || undefined,
    },
    ...(serviceAddress
      ? { serviceAddress }
      : shouldClearServiceAddress
        ? { serviceAddress: null }
        : {}),
    lineItems: lineItems.map<EstimateLineItem>((item, index) =>
      buildEstimateLinePayload(item, index, lineItemPreviews[index]),
    ),
    subtotal,
    taxTotal,
    discountTotal: discountAmount,
    discountValue,
    promotionId: promotionId ?? null,
    ...(discountAmount > 0 && {
      discountType: (discountType === '%' ? 'PERCENT' : 'FLAT') as 'PERCENT' | 'FLAT',
    }),
    ...(discountReason.trim() && { discountReason: discountReason.trim() }),
    pricingMode,
    ...(customerApprovalMethod && { customerApprovalMethod }),
    signatureRequired,
    ...(billingAddress
      ? { billingAddress }
      : shouldClearBillingAddress
        ? { billingAddress: null }
        : {}),
    ...(metadata && { metadata }),
    grandTotal,
    ...((isEditMode || depositRequired || depositValue > 0 || depositAmount > 0) && {
      depositAmount: depositAmount > 0 ? depositAmount : 0,
      depositRequired,
      depositType: (depositType === '%' ? 'PERCENT' : 'FLAT') as 'PERCENT' | 'FLAT',
      depositValue,
    }),
    bookingType,
    recurringConfig: recurringConfigPayload,
    // The selection always goes on the wire (both modes): the server re-resolves
    // it and re-snapshots the text, so switching back to "default" has to be
    // expressible as a payload, which an omit-when-empty guard would swallow.
    ...toTermsPayload(terms),
    // Estimate Template Builder — the chosen document template (null = tenant
    // default). Always on the wire so clearing it back to "Default" is
    // expressible; the server writes `dto.docTemplateId ?? null` on create and
    // only when the key is present on update.
    docTemplateId: docTemplateId ?? null,
    ...(isEditMode
      ? {
          validUntil: validUntil || null,
          scopeOfWork: scopeOfWork.trim(),
          notes: notes.trim(),
          internalNotes: internalNotes.trim(),
        }
      : {
          ...(validUntil && { validUntil }),
          ...(scopeOfWork.trim() && { scopeOfWork: scopeOfWork.trim() }),
          ...(notes.trim() && { notes: notes.trim() }),
          ...(internalNotes.trim() && { internalNotes: internalNotes.trim() }),
        }),
    tagIds,
    ...businessUnitFields,
    ...selectedBusinessUnitFields,
    ...(estimateNumber.trim() && { estimateNumber: estimateNumber.trim() }),
    // NOT-584 — on edit, "Not specified" has to travel as an explicit null or
    // the cleared job type is simply omitted and the estimate keeps its old
    // one. The name snapshot goes with it ('' clears server-side), else the
    // {job_type} title token keeps rendering a type the estimate no longer has.
    ...(jobTypeId
      ? { jobTypeId, jobTypeName: jobTypeName.trim() || undefined }
      : isEditMode
        ? { jobTypeId: null, jobTypeName: '' }
        : {}),
    ...(isEditMode
      ? { poNumber: poNumber.trim() }
      : poNumber.trim()
        ? { poNumber: poNumber.trim() }
        : {}),
    ...(includeTitle
      ? isEditMode
        ? { title: title.trim() }
        : title.trim()
          ? { title: title.trim() }
          : {}
      : {}),
    ...(includePendingAttachmentId && pendingAttachmentId ? { pendingAttachmentId } : {}),
  };
}

export function buildEstimateCustomFieldValues(
  customFields: Record<string, string>,
  estimateCustomFields: CustomFieldSchema[],
  activeFieldKeys?: string[],
): { fieldDefinitionId: string; value: unknown }[] {
  return buildCustomFieldValues(customFields, estimateCustomFields, [], activeFieldKeys);
}

interface PersistEstimateCustomFieldValuesArgs {
  estimateId: string;
  isEditMode: boolean;
  customFieldValues: { fieldDefinitionId: string; value: unknown }[];
  onInvalidateEditValues: (estimateId: string) => void;
  onCreateValues: (args: {
    entityType: 'estimate';
    entityId: string;
    values: { fieldDefinitionId: string; value: unknown }[];
  }) => Promise<unknown>;
}

export async function persistEstimateCustomFieldValues({
  estimateId,
  isEditMode,
  customFieldValues,
  onInvalidateEditValues,
  onCreateValues,
}: PersistEstimateCustomFieldValuesArgs): Promise<void> {
  if (customFieldValues.length === 0) return;

  if (isEditMode) {
    await apiPatch(operationsClient, opsEstimateEndpoints.customFieldValues(estimateId), {
      values: customFieldValues,
    });
    onInvalidateEditValues(estimateId);
    return;
  }

  await onCreateValues({
    entityType: 'estimate',
    entityId: estimateId,
    values: customFieldValues,
  });
}

interface CopyEstimateSourceAttachmentsArgs {
  estimateId: string;
  templateId?: string;
  excludedTemplateAttachmentIds?: string[];
  duplicateId?: string;
  excludedDuplicateAttachmentIds?: string[];
}

export async function copyEstimateSourceAttachments({
  estimateId,
  templateId,
  excludedTemplateAttachmentIds = [],
  duplicateId,
  excludedDuplicateAttachmentIds = [],
}: CopyEstimateSourceAttachmentsArgs): Promise<void> {
  if (templateId) {
    await apiPost(operationsClient, estimateTemplateEndpoints.copyAttachments(templateId), {
      estimateId,
      excludeIds: excludedTemplateAttachmentIds,
    });
  }

  if (duplicateId) {
    await apiPost(operationsClient, opsEstimateEndpoints.copyAttachments(duplicateId), {
      estimateId,
      excludeIds: excludedDuplicateAttachmentIds,
    });
  }
}

export function getEstimateEntityId(data: unknown, fallback = ''): string {
  if (!data || typeof data !== 'object') return fallback;
  const maybeData = data as { _id?: string; id?: string };
  return maybeData._id ?? maybeData.id ?? fallback;
}

interface FinalizeEstimateSaveArgs extends PersistEstimateCustomFieldValuesArgs {
  templateId?: string;
  excludedTemplateAttachmentIds?: string[];
  duplicateId?: string;
  excludedDuplicateAttachmentIds?: string[];
  copyAttachments?: boolean;
  onCopyAttachmentsError?: (error: unknown) => void;
}

export async function finalizeEstimateSave({
  estimateId,
  isEditMode,
  customFieldValues,
  onInvalidateEditValues,
  onCreateValues,
  templateId,
  excludedTemplateAttachmentIds,
  duplicateId,
  excludedDuplicateAttachmentIds,
  copyAttachments = true,
  onCopyAttachmentsError,
}: FinalizeEstimateSaveArgs): Promise<void> {
  await persistEstimateCustomFieldValues({
    estimateId,
    isEditMode,
    customFieldValues,
    onInvalidateEditValues,
    onCreateValues,
  });

  if (!copyAttachments) return;

  try {
    await copyEstimateSourceAttachments({
      estimateId,
      templateId,
      excludedTemplateAttachmentIds,
      duplicateId,
      excludedDuplicateAttachmentIds,
    });
  } catch (error) {
    onCopyAttachmentsError?.(error);
  }
}
